import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Typography,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  Tabs,
  Tab,
  CircularProgress,
  Autocomplete,
  Pagination,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Work,
  Home,
  Person,
  Schedule,
  Image as ImageIcon,
  Undo,
  Map as MapIcon,
  List as ListIcon,
  PhotoCamera,
  Close,
  AttachMoney,
  Visibility,
  ArrowUpward,
  ArrowDownward,
  Star,
  History,
} from '@mui/icons-material';
import api from '../../services/authService';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { exportToExcel, exportToPDF } from '../../utils/exportHelpers';
import { useLanguage } from '../../contexts/LanguageContext';
import { useThemeContext } from '../../contexts/ThemeContext';
import { extractAndTranslateError, formatNumber, formatCurrency, formatStringWithNumbers, formatDate, formatDateTime } from '../../utils/i18nHelpers';

interface Building {
  id: number;
  name: string;
  name_ar?: string;
  latitude?: number;
  longitude?: number;
}

interface Flat {
  id: number;
  flat_number: string;
  building_id?: number;
  building?: {
    id: number;
    name: string;
    name_ar?: string;
    latitude?: number;
    longitude?: number;
  };
}

interface Technician {
  id: number;
  service_type: string;
  company?: string;
  user?: {
    id: number;
    name: string;
    name_ar?: string;
    email: string;
  } | null;
}

interface SimpleUser {
  id: number;
  name: string;
  email: string;
}

interface Amenity {
  id: number;
  name_en: string;
  name_ar: string;
  is_active?: boolean;
}

interface WorkOrder {
  id: number;
  work_order_number: string;
  title: string;
  title_ar?: string;
  description: string;
  priority: 'low' | 'medium' | 'urgent';
  status: 'pending' | 'in_progress' | 'paused' | 'completed' | 'cancelled';
  scheduled_from?: string;
  scheduled_to?: string;
  completed_date?: string;
  notes?: string;
  images?: string[];
  created_at?: string;
  flat?: Flat;
  flats?: Flat[];
  amenities?: Amenity[];
  technician?: Technician;
  items?: Array<{
    id: number;
    item_name: string;
    category?: string;
    pivot?: { quantity: number };
  }>;
}

// Google Maps component for work orders
interface WorkOrdersMapProps {
  workOrders: WorkOrder[];
  onWorkOrderClick: (id: number) => void;
}

// Global flag to prevent multiple script loads
let googleMapsScriptLoaded = false;
let googleMapsLoadingPromise: Promise<void> | null = null;

// Google Maps types - merged into single declaration
declare global {
  interface Window {
    google?: any;
    googleMapsErrorHandler?: () => void;
    workOrderClickHandler?: (id: number) => void;
  }
}

const WorkOrdersMapComponent: React.FC<WorkOrdersMapProps> = ({ workOrders, onWorkOrderClick }) => {
  const { language } = useLanguage();
  const { themeColor } = useThemeContext();
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const infoWindowsRef = useRef<any[]>([]);
  const observerRef = useRef<MutationObserver | null>(null);

  useEffect(() => {
    if (!mapRef.current) return;

    // Hide Google Maps error messages that appear even when map is functional
    const hideGoogleMapsErrors = () => {
      const errorSelectors = [
        '.gm-err-container',
        '.gm-err-message',
        'div[style*="This page can\'t load Google Maps correctly"]',
        'div[style*="can\'t load Google Maps"]',
      ];

      errorSelectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach((el: any) => {
          if (el) {
            el.style.display = 'none';
            el.style.visibility = 'hidden';
            el.style.opacity = '0';
          }
        });
      });
    };

    // Set up MutationObserver to hide error messages as they appear
    if (mapRef.current) {
      observerRef.current = new MutationObserver(() => {
        hideGoogleMapsErrors();
      });

      observerRef.current.observe(document.body, {
        childList: true,
        subtree: true,
      });

      // Initial check
      hideGoogleMapsErrors();
    }
    
    // Debug: Log work orders data
    console.log('WorkOrdersMapComponent - workOrders:', workOrders);
    const validOrders = workOrders.filter(wo => wo.flat?.building?.latitude && wo.flat?.building?.longitude);
    console.log('WorkOrdersMapComponent - workOrders with locations:', validOrders.length);
    
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyB1EiZd7oGK4fnmgbeOXyFmWOeCoJmKrAc';

    // Set up error handler to suppress Google Maps warnings
    window.googleMapsErrorHandler = () => {
      // Suppress the error message if map is functional
      // This is a common issue with API key restrictions but map still works
      console.log('Google Maps API warning suppressed - map is functional');
    };

    // Load Google Maps script if not already loaded (prevent multiple loads)
    const loadGoogleMaps = (): Promise<void> => {
      if (window.google && window.google.maps) {
        return Promise.resolve();
      }

      if (googleMapsLoadingPromise) {
        return googleMapsLoadingPromise;
      }

      if (googleMapsScriptLoaded) {
        return Promise.resolve();
      }

      googleMapsLoadingPromise = new Promise<void>((resolve, reject) => {
        // Check if script already exists
        const existingScript = document.querySelector(`script[src*="maps.googleapis.com"]`);
        if (existingScript) {
          // Wait for it to load
          const checkInterval = setInterval(() => {
            if (window.google && window.google.maps) {
              clearInterval(checkInterval);
              googleMapsScriptLoaded = true;
              googleMapsLoadingPromise = null;
              resolve();
            }
          }, 100);
          setTimeout(() => {
            clearInterval(checkInterval);
            if (!window.google || !window.google.maps) {
              reject(new Error('Google Maps script timeout'));
            }
          }, 10000);
          return;
        }

        // Generate a unique callback name
        const callbackName = `googleMapsInit_${Date.now()}`;
        (window as any)[callbackName] = () => {
          googleMapsScriptLoaded = true;
          googleMapsLoadingPromise = null;
          delete (window as any)[callbackName];
          resolve();
        };

        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=${callbackName}`;
        script.async = true;
        script.defer = true;
        script.onerror = () => {
          googleMapsLoadingPromise = null;
          delete (window as any)[callbackName];
          reject(new Error('Failed to load Google Maps'));
        };
        document.head.appendChild(script);
      });

      return googleMapsLoadingPromise;
    };

    loadGoogleMaps().then(() => {
      if (!mapRef.current || !window.google || !window.google.maps) {
        console.error('Google Maps not available');
        return;
      }

      // Calculate center point (average of all locations)
      if (validOrders.length === 0) {
        // Show default map centered on Kuwait if no locations
        if (!mapInstanceRef.current) {
          mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
            center: { lat: 29.3759, lng: 47.9774 }, // Kuwait City
            zoom: 10,
            mapTypeControl: true,
            streetViewControl: true,
            fullscreenControl: true,
          });
        }
        return;
      }

      const avgLat = validOrders.reduce((sum, wo) => sum + (wo.flat!.building!.latitude || 0), 0) / validOrders.length;
      const avgLng = validOrders.reduce((sum, wo) => sum + (wo.flat!.building!.longitude || 0), 0) / validOrders.length;

      // Initialize map
      if (!mapInstanceRef.current) {
        mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
          center: { lat: avgLat, lng: avgLng },
          zoom: 12,
          mapTypeControl: true,
          streetViewControl: true,
          fullscreenControl: true,
        });
      } else {
        mapInstanceRef.current.setCenter({ lat: avgLat, lng: avgLng });
      }

      // Clear existing markers
      markersRef.current.forEach((marker: any) => {
        if (marker && marker.setMap) {
          marker.setMap(null);
        }
      });
      infoWindowsRef.current.forEach((infoWindow: any) => {
        if (infoWindow && infoWindow.close) {
          infoWindow.close();
        }
      });
      markersRef.current = [];
      infoWindowsRef.current = [];

      // Store click handler globally for info window button
      window.workOrderClickHandler = onWorkOrderClick;

      // Create markers for each work order
      validOrders.forEach((wo) => {
        if (!wo.flat?.building?.latitude || !wo.flat?.building?.longitude) return;
        if (!mapInstanceRef.current) return;

        const position = {
          lat: wo.flat.building.latitude,
          lng: wo.flat.building.longitude,
        };

        // Choose marker color based on status
        const getMarkerColor = (status: string) => {
          switch (status) {
            case 'pending': return '#FF9800'; // Orange
            case 'in_progress': return '#2196F3'; // Blue
            case 'completed': return '#4CAF50'; // Green
            case 'cancelled': return '#F44336'; // Red
            default: return '#757575'; // Grey
          }
        };

        // Create custom marker icon
        const markerIcon = {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: getMarkerColor(wo.status),
          fillOpacity: 1,
          strokeColor: '#FFFFFF',
          strokeWeight: 2,
        };

        try {
          const displayTitle = language === 'ar' && wo.title_ar ? wo.title_ar : wo.title;
          const displayBuildingName = language === 'ar' && wo.flat?.building?.name_ar ? wo.flat.building.name_ar : (wo.flat?.building?.name || 'Unknown');
          
          const marker = new window.google.maps.Marker({
            position,
            map: mapInstanceRef.current,
            title: displayTitle,
            icon: markerIcon,
          });

          // Create info window
          const infoWindow = new window.google.maps.InfoWindow({
            content: `
              <div style="padding: 8px; min-width: 200px;">
                <h3 style="margin: 0 0 8px 0; font-size: 16px; font-weight: bold;">${displayTitle}</h3>
                <p style="margin: 4px 0; font-size: 14px; color: #666;">
                  <strong>Building:</strong> ${displayBuildingName}<br/>
                  <strong>Flat:</strong> ${wo.flats && wo.flats.length > 0 ? wo.flats.map(f => f.flat_number).join(', ') : (wo.flat?.flat_number || 'N/A')}<br/>
                  <strong>Status:</strong> ${wo.status === 'in_progress' ? 'In Progress' : wo.status.charAt(0).toUpperCase() + wo.status.slice(1)}<br/>
                  <strong>Priority:</strong> ${wo.priority.charAt(0).toUpperCase() + wo.priority.slice(1)}<br/>
                  <strong>WO#:</strong> ${wo.work_order_number}
                </p>
                <button onclick="window.workOrderClickHandler && window.workOrderClickHandler(${wo.id})" style="margin-top: 8px; padding: 6px 12px; background: ${themeColor}; color: white; border: none; border-radius: 4px; cursor: pointer;">
                  View Details
                </button>
              </div>
            `,
          });

          marker.addListener('click', () => {
            // Close all other info windows
            infoWindowsRef.current.forEach((iw: any) => {
              if (iw && iw.close) iw.close();
            });
            infoWindow.open(mapInstanceRef.current, marker);
          });

          markersRef.current.push(marker);
          infoWindowsRef.current.push(infoWindow);
        } catch (error) {
          console.error('Error creating marker:', error);
        }
      });

      // Fit bounds to show all markers
      if (markersRef.current.length > 0 && mapInstanceRef.current) {
        try {
          const bounds = new window.google.maps.LatLngBounds();
          let hasValidBounds = false;
          
          markersRef.current.forEach((marker: any) => {
            if (marker && marker.getPosition) {
              const pos = marker.getPosition();
              if (pos) {
                bounds.extend(pos);
                hasValidBounds = true;
              }
            }
          });

          if (hasValidBounds) {
            mapInstanceRef.current.fitBounds(bounds);
            // Limit max zoom
            const listener = window.google.maps.event.addListener(mapInstanceRef.current, 'bounds_changed', () => {
              if (mapInstanceRef.current && mapInstanceRef.current.getZoom && mapInstanceRef.current.getZoom() > 15) {
                mapInstanceRef.current.setZoom(15);
              }
              window.google.maps.event.removeListener(listener);
            });
          }
        } catch (error) {
          console.error('Error fitting bounds:', error);
        }
      }
    }).catch(error => {
      console.error('Error loading Google Maps:', error);
    });

    // Cleanup on unmount
    return () => {
      markersRef.current.forEach((marker: any) => {
        if (marker && marker.setMap) {
          marker.setMap(null);
        }
      });
      infoWindowsRef.current.forEach((infoWindow: any) => {
        if (infoWindow && infoWindow.close) {
          infoWindow.close();
        }
      });
      // Disconnect observer
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [workOrders, onWorkOrderClick]);

  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
      <Box ref={mapRef} sx={{ width: '100%', height: '100%', borderRadius: 1 }} />
      {/* Hide Google Maps error overlay - map is functional but API key may have restrictions */}
      <style>{`
        .gm-err-container,
        .gm-err-message,
        .gm-style-cc,
        div[style*="This page can't load Google Maps correctly"] {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
        }
      `}</style>
    </Box>
  );
};

const WorkOrders: React.FC = () => {
  const { can, user, technicianId } = useAuth();
  const { notifySuccess, notifyError } = useNotification();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [flats, setFlats] = useState<Flat[]>([]);
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [amenities, setAmenities] = useState<Amenity[]>([]);
  const [buildingAmenities, setBuildingAmenities] = useState<Amenity[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const currentPage = parseInt(searchParams.get('page') || '1', 10) || 1;
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const perPage = 10;
  const [open, setOpen] = useState(false);
  const [editingWorkOrder, setEditingWorkOrder] = useState<WorkOrder | null>(null);
  const [checkDistance, setCheckDistance] = useState<number>(300);
  const currentCheckDistanceRef = useRef<number>(300);
  const storageBase = (() => {
    const base = (api.defaults.baseURL as string) || '';
    return base.replace(/\/api\/?$/, '');
  })();
  const storageUrl = (path?: string) => {
    if (!path) return '';
    if (/^https?:\/\//i.test(path)) return path;
    return `${storageBase}/storage/${path}`;
  };
  const [formData, setFormData] = useState({
    building_id: '',
    flat_ids: [] as string[],
    amenity_ids: [] as string[],
    technician_ids: [] as string[],
    title: '',
    title_ar: '',
    description: '',
    priority: 'medium' as 'low' | 'medium' | 'urgent',
    status: 'pending' as 'pending' | 'in_progress' | 'paused' | 'completed' | 'cancelled',
    scheduled_from: '',
    scheduled_to: '',
    notes: '',
  });
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [imagePreview, setImagePreview] = useState<string[]>([]);
  const [existingImages, setExistingImages] = useState<string[]>([]);

  // Check-In/Out state
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [checkOutOpen, setCheckOutOpen] = useState(false);
  const [activeWO, setActiveWO] = useState<WorkOrder | null>(null);
  const [beforeImages, setBeforeImages] = useState<File[]>([]);
  const [afterImages, setAfterImages] = useState<File[]>([]);
  const [beforePreview, setBeforePreview] = useState<string[]>([]);
  const [afterPreview, setAfterPreview] = useState<string[]>([]);
  const [location, setLocation] = useState<{ lat: string; lng: string }>({ lat: '', lng: '' });
  const [checkInNotes, setCheckInNotes] = useState<string>('');
  const [checkOutNotes, setCheckOutNotes] = useState<string>('');
  const [checkInDisplayTime, setCheckInDisplayTime] = useState<string>('');
  const [checkOutDisplayTime, setCheckOutDisplayTime] = useState<string>('');
  const [pauseOpen, setPauseOpen] = useState(false);
  const [resumeOpen, setResumeOpen] = useState(false);
  const [pauseNotes, setPauseNotes] = useState('');
  const [resumeNotes, setResumeNotes] = useState('');
  const [pauseLocation, setPauseLocation] = useState<{ lat: string; lng: string }>({ lat: '', lng: '' });
  const [resumeLocation, setResumeLocation] = useState<{ lat: string; lng: string }>({ lat: '', lng: '' });
  const [pauseDisplayTime, setPauseDisplayTime] = useState('');
  const [resumeDisplayTime, setResumeDisplayTime] = useState('');
  
  // Quick Check-In/Out state (standalone, no work order)
  const [quickCheckInOpen, setQuickCheckInOpen] = useState(false);
  const [quickCheckOutOpen, setQuickCheckOutOpen] = useState(false);
  const [activeQuickCheckIn, setActiveQuickCheckIn] = useState<any>(null);
  const [nearbyBuildings, setNearbyBuildings] = useState<Building[]>([]);
  const [quickBuilding, setQuickBuilding] = useState<Building | null>(null);
  const [quickFlatIds, setQuickFlatIds] = useState<number[]>([]);
  const [quickAmenityIds, setQuickAmenityIds] = useState<number[]>([]);
  const [quickBuildingFlats, setQuickBuildingFlats] = useState<Flat[]>([]);
  const [quickBuildingAmenities, setQuickBuildingAmenities] = useState<Amenity[]>([]);
  const [quickNotes, setQuickNotes] = useState('');
  const [quickCheckOutNotes, setQuickCheckOutNotes] = useState('');
  const [quickLocation, setQuickLocation] = useState<{ lat: string; lng: string }>({ lat: '', lng: '' });
  const [quickBeforeImages, setQuickBeforeImages] = useState<File[]>([]);
  const [quickBeforePreview, setQuickBeforePreview] = useState<string[]>([]);
  const [quickAfterImages, setQuickAfterImages] = useState<File[]>([]);
  const [quickAfterPreview, setQuickAfterPreview] = useState<string[]>([]);
  const [quickSubmitting, setQuickSubmitting] = useState(false);

  // Quick check-in history state
  const [checkInHistory, setCheckInHistory] = useState<any[]>([]);
  const [checkInHistoryPage, setCheckInHistoryPage] = useState(1);
  const [checkInHistoryTotalPages, setCheckInHistoryTotalPages] = useState(1);

  // Camera capture state
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraMode, setCameraMode] = useState<'before' | 'after'>('before');
  const [stream, setStream] = useState<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  // Effect to handle video stream when it changes
  useEffect(() => {
    if (stream && videoRef.current) {
      const video = videoRef.current;
      video.srcObject = stream;
      
      // Ensure video plays when metadata is loaded
      const handleLoadedMetadata = () => {
        video.play().catch(err => {
          console.error('Error playing video:', err);
        });
      };
      
      // Ensure video plays when it can play
      const handleCanPlay = () => {
        video.play().catch(err => {
          console.error('Error playing video:', err);
        });
      };
      
      video.addEventListener('loadedmetadata', handleLoadedMetadata);
      video.addEventListener('canplay', handleCanPlay);
      
      // Try to play immediately
      video.play().catch(err => {
        console.log('Video not ready yet, will play when loaded:', err);
      });
      
      return () => {
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
        video.removeEventListener('canplay', handleCanPlay);
      };
    } else if (videoRef.current && !stream) {
      videoRef.current.srcObject = null;
    }
  }, [stream, cameraOpen]);
  
  // Assign products
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignWO, setAssignWO] = useState<WorkOrder | null>(null);
  const [inventory, setInventory] = useState<Array<{ id: number; item_name: string; category: string }>>([]);
  const [selectedItemId, setSelectedItemId] = useState<string>('');
  const [selectedQty, setSelectedQty] = useState<string>('1');
  const [returnOpen, setReturnOpen] = useState(false);
  const [returnItem, setReturnItem] = useState<{ workOrder: WorkOrder; item: any } | null>(null);
  const [returnQty, setReturnQty] = useState<string>('');
  const [returnNotes, setReturnNotes] = useState<string>('');
  const [activeTab, setActiveTab] = useState(0);
  const statusParam = searchParams.get('status');
  const statusFilter: 'all' | 'pending' | 'in_progress' | 'paused' | 'completed' | 'cancelled' =
    statusParam && ['all', 'pending', 'in_progress', 'paused', 'completed', 'cancelled'].includes(statusParam)
      ? statusParam as any
      : 'all';
  const [timeFilter, setTimeFilter] = useState<'all' | 'lastHour' | 'last24Hours' | 'last7Days' | 'last30Days' | 'last90Days' | 'lastYear' | 'old'>('all');
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [activityFilter, setActivityFilter] = useState<'all' | 'check_in' | 'check_out' | 'pause' | 'resume'>('all');
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
  });

  useEffect(() => {
    fetchWorkOrders(currentPage, statusFilter, searchTerm);
    fetchActiveQuickCheckIn();
    // Fetch buildings if user has buildings.view OR work-orders.add OR work-orders.edit OR quick-check-ins.add (needed for quick check-in)
    if (can('buildings.view') || can('work-orders.add') || can('work-orders.edit') || can('quick-check-ins.add')) {
      fetchBuildings();
    }
    // Fetch flats if user has flats.view OR work-orders.add OR work-orders.edit (needed for form)
    if (can('flats.view') || can('work-orders.add') || can('work-orders.edit')) {
      fetchFlats();
    }
    // Fetch technicians if user has technicians.view OR work-orders.add OR work-orders.edit (needed for form)
    if (can('technicians.view') || can('work-orders.add') || can('work-orders.edit')) {
      fetchTechnicians();
    }
    // Fetch amenities for work order form and quick check-in
    if (can('work-orders.add') || can('work-orders.edit') || can('quick-check-ins.add')) {
      fetchAmenities();
    }
    // Fetch allowed distance setting
    (async () => {
      try {
        const { data } = await api.get('/settings/check-distance');
        if (data?.value) {
          const distance = Number(data.value);
          currentCheckDistanceRef.current = distance;
          setCheckDistance(distance);
        }
      } catch (e) {
        console.error('Failed to fetch check distance setting', e);
      }
    })();
  }, []);

  // Fetch fresh distance whenever any modal opens
  useEffect(() => {
    if (checkInOpen || checkOutOpen || pauseOpen || resumeOpen) {
      const fetchDistance = async () => {
        try {
          const { data } = await api.get('/settings/check-distance');
          const distance = data?.value ? Number(data.value) : 300;
          console.log('useEffect: Fetched check distance from API:', distance, 'Raw data:', data);
          currentCheckDistanceRef.current = distance;
          setCheckDistance(distance);
        } catch (e) {
          console.error('Failed to fetch check distance setting in useEffect', e);
        }
      };
      fetchDistance();
    }
  }, [checkInOpen, checkOutOpen, pauseOpen, resumeOpen]);

  const fetchWorkOrders = async (page: number = 1, status: string = statusFilter, search: string = searchTerm, from: string = dateFrom, to: string = dateTo, activity: string = activityFilter) => {
    try {
      let url = `/work-orders?page=${page}&per_page=${perPage}`;
      if (status && status !== 'all') {
        url += `&status=${status}`;
      }
      if (search && search.trim()) {
        url += `&search=${encodeURIComponent(search.trim())}`;
      }
      if (from) {
        url += `&date_from=${encodeURIComponent(from)}`;
      }
      if (to) {
        url += `&date_to=${encodeURIComponent(to)}`;
      }
      if (activity && activity !== 'all') {
        url += `&activity_type=${encodeURIComponent(activity)}`;
      }
      const response = await api.get(url);
      // Handle paginated response
      const responseData = response.data;
      const orders = responseData?.data || (Array.isArray(responseData) ? responseData : []);

      // Set pagination data if available
      if (responseData?.last_page) {
        setTotalPages(responseData.last_page);
        setTotalCount(responseData.total || 0);
        // no-op: page is derived from URL, updated only by user actions
      } else {
        setTotalPages(1);
        setTotalCount(orders.length);
      }

      console.log('Fetched work orders:', orders);
      if (orders.length > 0) {
        console.log('First work order:', orders[0]);
        if (orders[0].flat?.building) {
          console.log('Building data:', {
            id: orders[0].flat.building.id,
            name: orders[0].flat.building.name,
            latitude: orders[0].flat.building.latitude,
            longitude: orders[0].flat.building.longitude,
          });
        } else {
          console.log('No building data in first work order');
        }
      }
      setWorkOrders(orders);
    } catch (error) {
      console.error('Error fetching work orders:', error);
      setWorkOrders([]);
      setTotalPages(1);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchBuildings = async () => {
    // Always try to fetch if user can add/edit work orders or is a technician (needed for quick check-in)
    const isTechnician = user?.role === 'technician' || !!technicianId;
    if (!can('buildings.view') && !can('work-orders.add') && !can('work-orders.edit') && !isTechnician) {
      setBuildings([]);
      return;
    }
    
    try {
      const response = await api.get('/buildings');
      setBuildings(Array.isArray(response.data) ? response.data : []);
    } catch (error: any) {
      // Suppress 403 errors - expected when user doesn't have buildings.view permission
      // but still needs buildings for work order form
      if (error?.response?.status === 403) {
        // Silently fail - user doesn't have permission to view buildings
        // but they can still create/edit work orders without selecting a building
        setBuildings([]);
      } else {
        console.error('Error fetching buildings:', error);
        setBuildings([]);
      }
    }
  };

  const fetchFlats = async () => {
    // Always try to fetch if user can add/edit work orders (needed for form dropdowns)
    // Even if they don't have flats.view permission
    if (!can('flats.view') && !can('work-orders.add') && !can('work-orders.edit')) {
      setFlats([]);
      return;
    }
    
    try {
      const response = await api.get('/flats');
      const data = Array.isArray(response.data) ? response.data : [];
      data.sort((a: Flat, b: Flat) => a.flat_number.localeCompare(b.flat_number, undefined, { numeric: true }));
      setFlats(data);
    } catch (error: any) {
      // Suppress 403 errors - expected when user doesn't have flats.view permission
      // but still needs flats for work order form
      if (error?.response?.status === 403) {
        // Silently fail - user doesn't have permission to view flats
        // but they can still create/edit work orders without selecting a flat
        setFlats([]);
      } else {
      console.error('Error fetching flats:', error);
      setFlats([]);
      }
    }
  };

  const fetchFlatsByBuilding = async (buildingId: string) => {
    // Always try to fetch if user can add/edit work orders (needed for form dropdowns)
    if (!can('flats.view') && !can('work-orders.add') && !can('work-orders.edit')) {
      setFlats([]);
      return;
    }
    
    try {
      const response = await api.get(`/buildings/${buildingId}/flats`);
      const data = Array.isArray(response.data) ? response.data : [];
      data.sort((a: Flat, b: Flat) => a.flat_number.localeCompare(b.flat_number, undefined, { numeric: true }));
      setFlats(data);
    } catch (error: any) {
      // Suppress 403 errors - expected when user doesn't have flats.view permission
      if (error?.response?.status === 403) {
        setFlats([]);
      } else {
        console.error('Error fetching flats by building:', error);
        setFlats([]);
      }
    }
  };

  // Fetch flats and amenities when building is selected in the form
  useEffect(() => {
    if (formData.building_id) {
      fetchFlatsByBuilding(formData.building_id);
      fetchBuildingAmenities(formData.building_id);
    } else {
      // When no building is selected, fetch all flats (for initial load)
      if (can('flats.view') || can('work-orders.add') || can('work-orders.edit')) {
        fetchFlats();
      }
      setBuildingAmenities([]);
    }
  }, [formData.building_id, buildings, amenities]);

  const fetchTechnicians = async () => {
    // Always try to fetch if user can add/edit work orders (needed for form dropdowns)
    // Even if they don't have technicians.view permission
    if (!can('technicians.view') && !can('work-orders.add') && !can('work-orders.edit')) {
      setTechnicians([]);
      return;
    }
    
    try {
      const response = await api.get('/technicians');
      setTechnicians(Array.isArray(response.data) ? response.data : []);
    } catch (error: any) {
      // Suppress 403 errors - expected when user doesn't have technicians.view permission
      // but still needs technicians for work order form
      if (error?.response?.status === 403) {
        // Silently fail - user doesn't have permission to view technicians
        // but they can still create/edit work orders without selecting a technician
        setTechnicians([]);
      } else {
        console.error('Error fetching technicians:', error);
        setTechnicians([]);
      }
    }
  };

  const fetchAmenities = async () => {
    try {
      const response = await api.get('/amenities');
      setAmenities(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching amenities:', error);
      setAmenities([]);
    }
  };

  const fetchBuildingAmenities = (buildingId: string) => {
    const building = buildings.find(b => b.id.toString() === buildingId);
    if (building && (building as any).amenities_names) {
      setBuildingAmenities((building as any).amenities_names);
    } else if (building && (building as any).amenities) {
      // If amenities_names not available, filter from all amenities
      const buildingAmenityIds = (building as any).amenities || [];
      const filtered = amenities.filter(a => buildingAmenityIds.includes(a.id));
      setBuildingAmenities(filtered);
    } else {
      setBuildingAmenities([]);
    }
  };


  const fetchInventory = async () => {
    try {
      const response = await api.get('/inventory');
      setInventory(Array.isArray(response.data) ? response.data : []);
    } catch (e) {
      setInventory([]);
    }
  };

  const toLocalInput = (iso?: string) => {
    if (!iso) return '';
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, '0');
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const mi = pad(d.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  };

  const getCurrentDateTimeLocal = () => {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const yyyy = now.getFullYear();
    const mm = pad(now.getMonth() + 1);
    const dd = pad(now.getDate());
    const hh = pad(now.getHours());
    const mi = pad(now.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  };

  const handleOpen = (workOrder?: WorkOrder) => {
    if (workOrder) {
      setEditingWorkOrder(workOrder);
      // Use flats array if available, otherwise fall back to single flat
      const flatIds = workOrder.flats && workOrder.flats.length > 0
        ? workOrder.flats.map(f => f.id.toString())
        : (workOrder.flat?.id ? [workOrder.flat.id.toString()] : []);
      // Get amenity IDs from work order
      const amenityIds = workOrder.amenities
        ? workOrder.amenities.map(a => a.id.toString())
        : [];
      setFormData({
        building_id: workOrder.flat?.building?.id?.toString() || '',
        flat_ids: flatIds,
        amenity_ids: amenityIds,
        technician_ids: workOrder.technician?.id ? [workOrder.technician.id.toString()] : [],
        title: workOrder.title,
        title_ar: workOrder.title_ar || '',
        description: workOrder.description,
        priority: workOrder.priority,
        status: workOrder.status,
        scheduled_from: toLocalInput(workOrder.scheduled_from),
        scheduled_to: toLocalInput(workOrder.scheduled_to),
        notes: workOrder.notes || '',
      });
      setExistingImages(workOrder.images || []);
      setImagePreview([]);
    } else {
      setEditingWorkOrder(null);
      setFormData({
        building_id: '',
        flat_ids: [],
        amenity_ids: [],
        technician_ids: [],
        title: '',
        title_ar: '',
        description: '',
        priority: 'medium',
        status: 'pending',
        scheduled_from: '',
        scheduled_to: '',
        notes: '',
      });
      setImagePreview([]);
      setExistingImages([]);
    }
    setSelectedImages([]);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingWorkOrder(null);
    setSelectedImages([]);
    setImagePreview([]);
    setExistingImages([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return; // Prevent double submission
    setSubmitting(true);
    try {
      const formDataToSend = new FormData();
      // Send flat_ids as array
      formData.flat_ids.forEach(flatId => {
        formDataToSend.append('flat_ids[]', flatId);
      });

      // Send amenity_ids as array
      formData.amenity_ids.forEach(amenityId => {
        formDataToSend.append('amenity_ids[]', amenityId);
      });

      // Send technician_ids as array for both create and edit
      formData.technician_ids.forEach((techId) => {
        formDataToSend.append('technician_ids[]', techId);
      });
      
      formDataToSend.append('title', formData.title);
      formDataToSend.append('title_ar', formData.title_ar);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('priority', formData.priority);
      formDataToSend.append('status', formData.status);
      if (formData.scheduled_from) formDataToSend.append('scheduled_from', formData.scheduled_from);
      if (formData.scheduled_to) formDataToSend.append('scheduled_to', formData.scheduled_to);
      if (formData.notes) formDataToSend.append('notes', formData.notes);

      if (editingWorkOrder) {
        if (existingImages.length > 0) {
          existingImages.forEach((image, index) => {
            formDataToSend.append(`existing_images[${index}]`, image);
          });
        }
        if (selectedImages.length > 0) {
          selectedImages.forEach((image, index) => {
            formDataToSend.append(`images[${index}]`, image);
          });
        }
        formDataToSend.append('_method', 'PUT');
        await api.post(`/work-orders/${editingWorkOrder.id}`, formDataToSend, { headers: { 'Content-Type': 'multipart/form-data' } });
        notifySuccess(t('workOrders.messages.save.successUpdate'));
      } else {
        selectedImages.forEach((image, index) => formDataToSend.append(`images[${index}]`, image));
        await api.post('/work-orders', formDataToSend, { headers: { 'Content-Type': 'multipart/form-data' } });
        notifySuccess(t('workOrders.messages.save.successCreate'));
      }
      fetchWorkOrders();
      handleClose();
    } catch (error: any) {
      console.error('Error saving work order:', error);
      const errorData = error?.response?.data;
      if (errorData?.error_key === 'duplicate_technician_assignment' && errorData?.duplicate_technicians) {
        const names = (errorData.duplicate_technicians as string[]).join(', ');
        notifyError(t('workOrders.messages.save.duplicateTechnician').replace('{{technicians}}', names));
      } else {
        const errorMessage = extractAndTranslateError(error, language, t);
        notifyError(errorMessage || t('workOrders.messages.save.errorDefault'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddCategory = async () => {
    // TODO: Implement category creation when backend API is available
    notifyError(t('workOrders.messages.category.notImplemented') || 'Category creation is not yet implemented');
    setCategoryDialogOpen(false);
    setCategoryForm({ name: '', description: '' });
  };

  const handleDelete = async (id: number) => {
    if (window.confirm(t('workOrders.messages.delete.confirm'))) {
      try {
        await api.delete(`/work-orders/${id}`);
        fetchWorkOrders(currentPage);
        notifySuccess(t('workOrders.messages.delete.success'));
      } catch (error) {
        console.error('Error deleting work order:', error);
        const errorMessage = extractAndTranslateError(error, language, t);
        notifyError(errorMessage || t('workOrders.messages.delete.errorDefault'));
      }
    }
  };

  const updateSearchParams = (page: number, status: string) => {
    const params: Record<string, string> = {};
    if (page > 1) params.page = page.toString();
    if (status !== 'all') params.status = status;
    setSearchParams(params, { replace: true });
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    updateSearchParams(page, statusFilter);
    fetchWorkOrders(page, statusFilter, searchTerm);
  };

  const handleStatusFilterChange = (newStatus: string) => {
    updateSearchParams(1, newStatus);
    fetchWorkOrders(1, newStatus, searchTerm);
  };

  // Debounced search via ref timer — no useEffect needed
  const searchTimerRef = useRef<NodeJS.Timeout | null>(null);
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      updateSearchParams(1, statusFilter);
      fetchWorkOrders(1, statusFilter, value);
    }, 300);
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setSelectedImages(prev => [...prev, ...files]);
    const newPreviews = files.map(file => URL.createObjectURL(file));
    setImagePreview(prev => [...prev, ...newPreviews]);
    e.target.value = '';
  };

  const removeExistingImage = (index: number) => {
    setExistingImages(prev => prev.filter((_, i) => i !== index));
  };

  const removeNewImage = (index: number) => {
    setImagePreview(prev => {
      const newPreviews = [...prev];
      const urlToRemove = newPreviews[index];
      URL.revokeObjectURL(urlToRemove);
      newPreviews.splice(index, 1);
      return newPreviews;
    });
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    const numericFields: string[] = [];

    if (type === 'number' && numericFields.includes(name)) {
      const sanitized = value === '' ? '' : Math.max(0, Number(value));
      setFormData(prev => ({ ...prev, [name]: sanitized }));
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const fetchCheckDistance = async (): Promise<number> => {
    try {
      const { data } = await api.get('/settings/check-distance');
      const distance = data?.value ? Number(data.value) : 300;
      console.log('Fetched check distance from API:', distance, 'Raw data:', data);
      currentCheckDistanceRef.current = distance;
      setCheckDistance(distance);
      return distance;
    } catch (e) {
      console.error('Failed to fetch check distance setting', e);
      const distance = 300; // Default fallback
      currentCheckDistanceRef.current = distance;
      setCheckDistance(distance);
      return distance;
    }
  };

  const handleCheckInOpen = async (wo: WorkOrder) => {
    setActiveWO(wo);
    setBeforeImages([]);
    setBeforePreview([]);
    setLocation({ lat: '', lng: '' });
    setCheckInNotes('');
    setCheckInDisplayTime(new Date().toLocaleString());
    // Fetch fresh check distance from database BEFORE opening modal
    await fetchCheckDistance();
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setLocation({ lat: pos.coords.latitude.toString(), lng: pos.coords.longitude.toString() }),
        async () => {
          try {
            const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string | undefined;
            if (apiKey) {
              const resp = await fetch(`https://www.googleapis.com/geolocation/v1/geolocate?key=${apiKey}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ considerIp: true }) });
              const data = await resp.json();
              if (data?.location) setLocation({ lat: data.location.lat.toString(), lng: data.location.lng.toString() });
            }
          } catch {}
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }
    setCheckInOpen(true);
  };

  const handleCheckOutOpen = async (wo: WorkOrder) => {
    setActiveWO(wo);
    setAfterImages([]);
    setAfterPreview([]);
    setCheckOutNotes('');
    setCheckOutDisplayTime(formatDateTime(new Date(), language));
    // Fetch fresh check distance from database before opening modal
    await fetchCheckDistance();
    // Clean up any existing camera stream
    if (stream) {
      stream.getTracks().forEach((track: MediaStreamTrack) => track.stop());
      setStream(null);
    }
    // Fetch current location for checkout as well
    setLocation({ lat: '', lng: '' });
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setLocation({ lat: pos.coords.latitude.toString(), lng: pos.coords.longitude.toString() }),
        async () => {
          try {
            const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string | undefined;
            if (apiKey) {
              const resp = await fetch(`https://www.googleapis.com/geolocation/v1/geolocate?key=${apiKey}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ considerIp: true }) });
              const data = await resp.json();
              if (data?.location) setLocation({ lat: data.location.lat.toString(), lng: data.location.lng.toString() });
            }
          } catch {}
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }
    setCheckOutOpen(true);
  };

  const handlePauseOpen = async (wo: WorkOrder) => {
    setActiveWO(wo);
    setPauseNotes('');
    setPauseLocation({ lat: '', lng: '' });
    setPauseDisplayTime(formatDateTime(new Date(), language));
    // Fetch fresh check distance from database before opening modal
    await fetchCheckDistance();
    setPauseOpen(true);
    getCurrentLatLng()
      .then((loc) => setPauseLocation(loc))
      .catch(() => notifyError(t('workOrders.location.error.pause')));
  };

  const handleResumeOpen = async (wo: WorkOrder) => {
    setActiveWO(wo);
    setResumeNotes('');
    setResumeLocation({ lat: '', lng: '' });
    setResumeDisplayTime(formatDateTime(new Date(), language));
    // Fetch fresh check distance from database before opening modal
    await fetchCheckDistance();
    setResumeOpen(true);
    getCurrentLatLng()
      .then((loc) => setResumeLocation(loc))
      .catch(() => notifyError(t('workOrders.location.error.resume')));
  };

  const onBeforeImages = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setBeforeImages(prev => [...prev, ...files]);
    const previews = files.map(f => URL.createObjectURL(f));
    setBeforePreview(prev => [...prev, ...previews]);
    e.target.value = '' as any;
  };

  const onAfterImages = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setAfterImages(prev => [...prev, ...files]);
    const previews = files.map(f => URL.createObjectURL(f));
    setAfterPreview(prev => [...prev, ...previews]);
    e.target.value = '' as any;
  };

  // Camera capture functions
  const openCamera = async (mode: 'before' | 'after') => {
    setCameraMode(mode);
    setCameraOpen(true);
    try {
      // Try back camera first (for mobile)
      let mediaStream: MediaStream | null = null;
      try {
        mediaStream = await navigator.mediaDevices.getUserMedia({ 
          video: { 
            facingMode: 'environment',
            width: { ideal: 1280 },
            height: { ideal: 720 }
          }
        });
      } catch (envError) {
        // Fallback to any available camera
        console.log('Back camera not available, trying default camera');
        try {
          mediaStream = await navigator.mediaDevices.getUserMedia({ 
            video: {
              width: { ideal: 1280 },
              height: { ideal: 720 }
            }
          });
        } catch (fallbackError) {
          // Last resort - any video
          mediaStream = await navigator.mediaDevices.getUserMedia({ 
            video: true
          });
        }
      }
      
      if (mediaStream) {
        setStream(mediaStream);
      } else {
        setCameraOpen(false);
        notifyError(t('workOrders.camera.error.access'));
      }
    } catch (error: any) {
      console.error('Error accessing camera:', error);
      setCameraOpen(false);
      const errorMessage = error?.name === 'NotAllowedError' 
        ? t('workOrders.camera.error.permission')
        : error?.name === 'NotFoundError'
        ? t('workOrders.camera.error.notFound')
        : t('workOrders.camera.error.generic');
      notifyError(errorMessage);
    }
  };

  const closeCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track: MediaStreamTrack) => track.stop());
      setStream(null);
    }
    setCameraOpen(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    
    if (!context) return;
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0);
    
    canvas.toBlob((blob: Blob | null) => {
      if (!blob) return;
      
      const file = new File([blob], `camera-${Date.now()}.jpg`, { type: 'image/jpeg' });
      
      if (cameraMode === 'before') {
        setBeforeImages(prev => [...prev, file]);
        setBeforePreview(prev => [...prev, URL.createObjectURL(file)]);
      } else {
        setAfterImages(prev => [...prev, file]);
        setAfterPreview(prev => [...prev, URL.createObjectURL(file)]);
      }
      
      closeCamera();
    }, 'image/jpeg', 0.9);
  };

  const submitCheckIn = async () => {
    if (!activeWO || !location.lat || !location.lng) return;
    
    // Fetch fresh check distance from database
    const maxMeters = await fetchCheckDistance();
    
    const buildingLat = activeWO.flat?.building?.latitude;
    const buildingLng = activeWO.flat?.building?.longitude;
    const lat = parseFloat(location.lat);
    const lng = parseFloat(location.lng);

    if (buildingLat == null || buildingLng == null) {
      notifyError(t('workOrders.checkIn.error.buildingLocation'));
      return;
    }
    if (isNaN(lat) || isNaN(lng)) {
      notifyError(t('workOrders.checkIn.error.currentLocation'));
      return;
    }
    const dist = distanceMeters(lat, lng, buildingLat, buildingLng);
    if (dist > maxMeters) {
      notifyError(`Check-in blocked: you are ${dist.toFixed(0)}m away (limit ${maxMeters}m).`);
      return;
    }

    const form = new FormData();
    form.append('check_in_location_lat', location.lat);
    form.append('check_in_location_lng', location.lng);
    if (checkInNotes) form.append('check_in_notes', checkInNotes);
    beforeImages.forEach((f, i) => form.append(`before_images[${i}]`, f));
    try {
      await api.post(`/work-orders/${activeWO.id}/check-in`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setCheckInOpen(false);
      fetchWorkOrders();
      notifySuccess(t('workOrders.checkIn.success'));
    } catch (e) {
      console.error('Check-in failed', e);
      const errorMessage = extractAndTranslateError(e, language, t);
      notifyError(errorMessage || t('workOrders.checkIn.failed'));
    }
  };

  const submitCheckOut = async () => {
    if (!activeWO) return;
    
    // Fetch fresh check distance from database
    const maxMeters = await fetchCheckDistance();
    
    const buildingLat = activeWO.flat?.building?.latitude;
    const buildingLng = activeWO.flat?.building?.longitude;
    const lat = location.lat ? parseFloat(location.lat) : NaN;
    const lng = location.lng ? parseFloat(location.lng) : NaN;

    if (buildingLat == null || buildingLng == null) {
      notifyError(t('workOrders.checkOut.error.buildingLocation'));
      return;
    }
    if (isNaN(lat) || isNaN(lng)) {
      notifyError(t('workOrders.checkOut.error.currentLocation'));
      return;
    }
    const dist = distanceMeters(lat, lng, buildingLat, buildingLng);
    if (dist > maxMeters) {
      notifyError(`Check-out blocked: you are ${dist.toFixed(0)}m away (limit ${maxMeters}m).`);
      return;
    }
    const form = new FormData();
    afterImages.forEach((f, i) => form.append(`after_images[${i}]`, f));
    if (checkOutNotes) form.append('check_out_notes', checkOutNotes);
    if (location.lat && location.lng) {
      form.append('check_out_location_lat', location.lat);
      form.append('check_out_location_lng', location.lng);
    }
    // Use POST with method override to ensure file uploads are parsed by PHP/Laravel
    form.append('_method', 'PATCH');
    try {
      await api.post(`/work-orders/${activeWO.id}/check-out`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setCheckOutOpen(false);
      setAfterImages([]);
      setAfterPreview([]);
      setCheckOutNotes('');
      fetchWorkOrders();
      notifySuccess(t('workOrders.checkOut.success'));
    } catch (e) {
      console.error('Check-out failed', e);
      const message = (e as any)?.response?.data?.error || t('workOrders.checkOut.failed');
      notifyError(message);
    }
  };

  const submitPause = async () => {
    if (!activeWO || !pauseLocation.lat || !pauseLocation.lng) {
      notifyError(t('workOrders.pause.error.location'));
      return;
    }
    
    // Fetch fresh check distance from database
    const maxMeters = await fetchCheckDistance();
    
    const buildingLat = activeWO.flat?.building?.latitude;
    const buildingLng = activeWO.flat?.building?.longitude;
    const lat = parseFloat(pauseLocation.lat);
    const lng = parseFloat(pauseLocation.lng);
    
    if (buildingLat != null && buildingLng != null && !isNaN(lat) && !isNaN(lng)) {
      const dist = distanceMeters(lat, lng, buildingLat, buildingLng);
      if (dist > maxMeters) {
        notifyError(`Pause blocked: you are ${dist.toFixed(0)}m away (limit ${maxMeters}m).`);
        return;
      }
    }
    
    try {
      await api.post(`/work-orders/${activeWO.id}/pause`, {
        notes: pauseNotes,
        lat: parseFloat(pauseLocation.lat),
        lng: parseFloat(pauseLocation.lng),
      });
      notifySuccess(t('workOrders.pause.confirm'));
      setPauseOpen(false);
      // Refresh work orders to get updated status
      fetchWorkOrders();
    } catch (error: any) {
      const message = error?.response?.data?.error || t('workOrders.pause.failed');
      notifyError(message);
    }
  };

  const submitResume = async () => {
    if (!activeWO || !resumeLocation.lat || !resumeLocation.lng) {
      notifyError(t('workOrders.resume.error.location'));
      return;
    }
    
    // Fetch fresh check distance from database
    const maxMeters = await fetchCheckDistance();
    
    const buildingLat = activeWO.flat?.building?.latitude;
    const buildingLng = activeWO.flat?.building?.longitude;
    const lat = parseFloat(resumeLocation.lat);
    const lng = parseFloat(resumeLocation.lng);
    
    if (buildingLat != null && buildingLng != null && !isNaN(lat) && !isNaN(lng)) {
      const dist = distanceMeters(lat, lng, buildingLat, buildingLng);
      if (dist > maxMeters) {
        notifyError(`Resume blocked: you are ${dist.toFixed(0)}m away (limit ${maxMeters}m).`);
        return;
      }
    }
    
    try {
      await api.post(`/work-orders/${activeWO.id}/resume`, {
        notes: resumeNotes,
        lat: parseFloat(resumeLocation.lat),
        lng: parseFloat(resumeLocation.lng),
      });
      notifySuccess(t('workOrders.resume.confirm'));
      setResumeOpen(false);
      // Refresh work orders to get updated status
      fetchWorkOrders();
    } catch (error: any) {
      const message = error?.response?.data?.error || t('workOrders.resume.failed');
      notifyError(message);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'default';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'info';
      case 'paused': return 'warning';
      case 'pending': return 'warning';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getCurrentLatLng = (): Promise<{ lat: string; lng: string }> => {
    return new Promise((resolve, reject) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => resolve({ lat: pos.coords.latitude.toString(), lng: pos.coords.longitude.toString() }),
          async () => {
            try {
              const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string | undefined;
              if (apiKey) {
                const resp = await fetch(`https://www.googleapis.com/geolocation/v1/geolocate?key=${apiKey}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ considerIp: true }) });
                const data = await resp.json();
                if (data?.location) return resolve({ lat: data.location.lat.toString(), lng: data.location.lng.toString() });
              }
            } catch {}
            reject(new Error('Unable to fetch location'));
          },
          { enableHighAccuracy: true, timeout: 8000 }
        );
      } else {
        reject(new Error('Geolocation not supported'));
      }
    });
  };

  // Haversine distance in meters
  const distanceMeters = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const toRad = (v: number) => (v * Math.PI) / 180;
    const R = 6371000; // meters
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // ---- Quick Check-In/Out (standalone, no work order) ----

  const fetchActiveQuickCheckIn = async () => {
    try {
      const { data } = await api.get('/check-ins/my-active');
      setActiveQuickCheckIn(Array.isArray(data) && data.length > 0 ? data[0] : null);
    } catch (e) {
      console.error('Failed to fetch active quick check-in', e);
    }
  };

  const fetchCheckInHistory = async (page: number = 1) => {
    try {
      const { data } = await api.get(`/check-ins/history?per_page=10&page=${page}`);
      setCheckInHistory(data?.data || []);
      setCheckInHistoryTotalPages(data?.last_page || 1);
      setCheckInHistoryPage(data?.current_page || page);
    } catch (e) {
      console.error('Failed to fetch check-in history', e);
      setCheckInHistory([]);
    }
  };

  const handleQuickCheckInOpen = async () => {
    try {
      const maxMeters = await fetchCheckDistance();
      const loc = await getCurrentLatLng();
      setQuickLocation(loc);

      const lat = parseFloat(loc.lat);
      const lng = parseFloat(loc.lng);

      // Filter buildings by GPS proximity
      const nearby = buildings.filter((b: Building) => {
        if (!b.latitude || !b.longitude) return false;
        const dist = distanceMeters(lat, lng, b.latitude, b.longitude);
        return dist <= maxMeters;
      });

      setNearbyBuildings(nearby);
      setQuickBuilding(null);
      setQuickFlatIds([]);
      setQuickAmenityIds([]);
      setQuickBuildingFlats([]);
      setQuickBuildingAmenities([]);
      setQuickNotes('');
      setQuickBeforeImages([]);
      setQuickBeforePreview([]);
      setQuickCheckInOpen(true);
    } catch (e) {
      console.error('Failed to open quick check-in', e);
      notifyError(t('workOrders.quickCheckIn.locationError'));
    }
  };

  const handleQuickBuildingSelect = async (building: Building | null) => {
    setQuickBuilding(building);
    setQuickFlatIds([]);
    setQuickAmenityIds([]);
    setQuickBuildingFlats([]);
    setQuickBuildingAmenities([]);

    if (building) {
      // Use amenities_names from the building object (already in list response)
      const bldg = building as any;
      if (bldg.amenities_names && Array.isArray(bldg.amenities_names)) {
        setQuickBuildingAmenities(bldg.amenities_names);
      }

      // Fetch flats for this building
      try {
        const { data: flatsData } = await api.get(`/buildings/${building.id}/flats`);
        const flatsList = Array.isArray(flatsData) ? flatsData : (flatsData?.data || []);
        setQuickBuildingFlats(flatsList);
      } catch (e) {
        console.error('Failed to fetch flats for building', e);
      }
    }
  };

  const submitQuickCheckIn = async () => {
    if (!quickBuilding || !quickLocation.lat || !quickLocation.lng || quickSubmitting) return;
    setQuickSubmitting(true);

    try {
      const fd = new FormData();
      fd.append('building_id', String(quickBuilding.id));
      fd.append('check_in_location_lat', quickLocation.lat);
      fd.append('check_in_location_lng', quickLocation.lng);
      if (quickNotes) fd.append('check_in_notes', quickNotes);
      quickFlatIds.forEach(id => fd.append('flat_ids[]', String(id)));
      quickAmenityIds.forEach(id => fd.append('amenity_ids[]', String(id)));
      quickBeforeImages.forEach((img, i) => fd.append(`before_images[${i}]`, img));

      const { data } = await api.post('/check-ins/quick', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setActiveQuickCheckIn(data);
      setQuickCheckInOpen(false);
      if (activeTab === 2) fetchCheckInHistory(checkInHistoryPage);
      notifySuccess(t('workOrders.quickCheckIn.success'));
    } catch (e: any) {
      console.error('Quick check-in failed', e);
      const msg = e?.response?.data?.error || t('workOrders.quickCheckIn.failed');
      notifyError(msg);
    } finally {
      setQuickSubmitting(false);
    }
  };

  const handleQuickCheckOutOpen = async () => {
    try {
      const loc = await getCurrentLatLng();
      setQuickLocation(loc);
      setQuickCheckOutNotes('');
      setQuickAfterImages([]);
      setQuickAfterPreview([]);
      setQuickCheckOutOpen(true);
    } catch (e) {
      console.error('Failed to get location for quick check-out', e);
      notifyError(t('workOrders.quickCheckIn.locationError'));
    }
  };

  const submitQuickCheckOut = async () => {
    if (!activeQuickCheckIn || quickSubmitting) return;
    setQuickSubmitting(true);

    try {
      const fd = new FormData();
      if (quickLocation.lat) fd.append('check_out_location_lat', quickLocation.lat);
      if (quickLocation.lng) fd.append('check_out_location_lng', quickLocation.lng);
      if (quickCheckOutNotes) fd.append('check_out_notes', quickCheckOutNotes);
      quickAfterImages.forEach((img, i) => fd.append(`after_images[${i}]`, img));

      await api.post(`/check-ins/${activeQuickCheckIn.id}/quick-check-out?_method=PATCH`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setActiveQuickCheckIn(null);
      setQuickCheckOutOpen(false);
      if (activeTab === 2) fetchCheckInHistory(checkInHistoryPage);
      notifySuccess(t('workOrders.quickCheckOut.success'));
    } catch (e: any) {
      console.error('Quick check-out failed', e);
      const msg = e?.response?.data?.error || t('workOrders.quickCheckOut.failed');
      notifyError(msg);
    } finally {
      setQuickSubmitting(false);
    }
  };

  const handleQuickBeforeImages = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setQuickBeforeImages(prev => [...prev, ...files]);
      const previews = files.map(f => URL.createObjectURL(f));
      setQuickBeforePreview(prev => [...prev, ...previews]);
    }
  };

  const handleQuickAfterImages = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setQuickAfterImages(prev => [...prev, ...files]);
      const previews = files.map(f => URL.createObjectURL(f));
      setQuickAfterPreview(prev => [...prev, ...previews]);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  // Note: Search and status filtering is now done on the backend for proper pagination
  // Only time filtering remains on frontend (could be moved to backend later)
  const filteredWorkOrders = workOrders.filter((wo) => {
    // Time-based filtering
    let matchesTime = true;
    if (timeFilter !== 'all' && wo.created_at) {
      const createdDate = new Date(wo.created_at);
      const now = new Date();
      const timeDiff = now.getTime() - createdDate.getTime();
      const hoursDiff = timeDiff / (1000 * 60 * 60);
      const daysDiff = hoursDiff / 24;
      
      switch (timeFilter) {
        case 'lastHour':
          matchesTime = hoursDiff <= 1; // Last hour
          break;
        case 'last24Hours':
          matchesTime = hoursDiff <= 24; // Last 24 hours
          break;
        case 'last7Days':
          matchesTime = daysDiff <= 7; // Last 7 days
          break;
        case 'last30Days':
          matchesTime = daysDiff <= 30; // Last 30 days
          break;
        case 'last90Days':
          matchesTime = daysDiff <= 90; // Last 90 days
          break;
        case 'lastYear':
          matchesTime = daysDiff <= 365; // Last year
          break;
        case 'old':
          matchesTime = daysDiff > 365; // Older than 1 year
          break;
        default:
          matchesTime = true;
      }
    }
    
    return matchesTime;
  }).sort((a, b) => {
    // Sort by created_at based on selected order
    const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
    const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
    return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
  });

  // Filter work orders that have building locations
  const workOrdersWithLocations = filteredWorkOrders.filter((wo: WorkOrder) => {
    const hasLocation = wo.flat?.building?.latitude && wo.flat?.building?.longitude;
    if (!hasLocation && wo.flat?.building) {
      console.log('Work order missing location:', {
        workOrderId: wo.id,
        workOrderTitle: wo.title,
        buildingId: wo.flat.building.id,
        buildingName: wo.flat.building.name,
        latitude: wo.flat.building.latitude,
        longitude: wo.flat.building.longitude,
        flat: wo.flat,
      });
    }
    return hasLocation;
  });
  
  console.log('Total work orders:', workOrders.length);
  console.log('Work orders with locations:', workOrdersWithLocations.length);

  const handleExportExcel = async () => {
    if (!filteredWorkOrders.length) {
      notifyError(t('workOrders.export.noData'));
      return;
    }
    const rows = filteredWorkOrders.map((wo) => ({
      [t('workOrders.export.columns.number')]: wo.work_order_number,
      [t('workOrders.export.columns.title')]: language === 'ar' && wo.title_ar ? wo.title_ar : wo.title,
      [t('workOrders.export.columns.description')]: wo.description || '',
      [t('workOrders.export.columns.status')]: t(`workOrders.status.${wo.status === 'in_progress' ? 'inProgress' : wo.status === 'paused' ? 'paused' : wo.status}`),
      [t('workOrders.export.columns.priority')]: t(`workOrders.priority.${wo.priority}`),
      [t('workOrders.export.columns.building')]: language === 'ar' && wo.flat?.building?.name_ar ? wo.flat.building.name_ar : (wo.flat?.building?.name || ''),
      [t('workOrders.export.columns.flat')]: wo.flats && wo.flats.length > 0 ? wo.flats.map(f => f.flat_number).join(', ') : (wo.flat?.flat_number || ''),
      [t('workOrders.export.columns.provider')]: (language === 'ar' && wo.technician?.user?.name_ar ? wo.technician.user.name_ar : wo.technician?.user?.name) || wo.technician?.company || '',
    }));
    await exportToExcel(rows, 'work_orders');
    notifySuccess(t('workOrders.export.excel.success'));
  };

  const handleExportPDF = async () => {
    if (!filteredWorkOrders.length) {
      notifyError(t('workOrders.export.noData'));
      return;
    }
    const columns = [
      t('workOrders.export.columns.number'),
      t('workOrders.export.columns.title'),
      t('workOrders.export.columns.description'),
      t('workOrders.export.columns.status'),
      t('workOrders.export.columns.priority'),
      t('workOrders.export.columns.building'),
      t('workOrders.export.columns.flat'),
      t('workOrders.export.columns.provider')
    ];
    const data = filteredWorkOrders.map((wo) => [
      wo.work_order_number,
      language === 'ar' && wo.title_ar ? wo.title_ar : wo.title,
      wo.description || '',
      t(`workOrders.status.${wo.status === 'in_progress' ? 'inProgress' : wo.status === 'paused' ? 'paused' : wo.status}`),
      t(`workOrders.priority.${wo.priority}`),
      language === 'ar' && wo.flat?.building?.name_ar ? wo.flat.building.name_ar : (wo.flat?.building?.name || ''),
      wo.flats && wo.flats.length > 0 ? wo.flats.map(f => f.flat_number).join(', ') : (wo.flat?.flat_number || ''),
      (language === 'ar' && wo.technician?.user?.name_ar ? wo.technician.user.name_ar : wo.technician?.user?.name) || wo.technician?.company || '',
    ]);
    await exportToPDF(columns, data, 'work_orders', language, t('workOrders.title'));
    notifySuccess(t('workOrders.export.pdf.success'));
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">{t('workOrders.title')}</Typography>
        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
          {activeTab !== 2 && (
            <>
              <TextField
                size="small"
                placeholder={t('workOrders.search.placeholder')}
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>{t('workOrders.filter.time')}</InputLabel>
                <Select
                  value={timeFilter}
                  label={t('workOrders.filter.time')}
                  onChange={(e) => setTimeFilter(e.target.value as 'all' | 'lastHour' | 'last24Hours' | 'last7Days' | 'last30Days' | 'last90Days' | 'lastYear' | 'old')}
                >
                  <MenuItem value="all">{t('workOrders.filter.timeAll')}</MenuItem>
                  <MenuItem value="lastHour">{t('workOrders.filter.lastHour')}</MenuItem>
                  <MenuItem value="last24Hours">{t('workOrders.filter.last24Hours')}</MenuItem>
                  <MenuItem value="last7Days">{t('workOrders.filter.last7Days')}</MenuItem>
                  <MenuItem value="last30Days">{t('workOrders.filter.last30Days')}</MenuItem>
                  <MenuItem value="last90Days">{t('workOrders.filter.last90Days')}</MenuItem>
                  <MenuItem value="lastYear">{t('workOrders.filter.lastYear')}</MenuItem>
                  <MenuItem value="old">{t('workOrders.filter.old')}</MenuItem>
                </Select>
              </FormControl>
              <TextField
                size="small"
                type="date"
                label={t('workOrders.filter.dateFrom')}
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  fetchWorkOrders(1, statusFilter, searchTerm, e.target.value, dateTo);
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 140 }}
              />
              <TextField
                size="small"
                type="date"
                label={t('workOrders.filter.dateTo')}
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  fetchWorkOrders(1, statusFilter, searchTerm, dateFrom, e.target.value);
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: dateFrom || undefined }}
                sx={{ minWidth: 140 }}
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>{t('workOrders.filter.activity')}</InputLabel>
                <Select
                  value={activityFilter}
                  label={t('workOrders.filter.activity')}
                  onChange={(e) => {
                    const val = e.target.value as 'all' | 'check_in' | 'check_out' | 'pause' | 'resume';
                    setActivityFilter(val);
                    fetchWorkOrders(1, statusFilter, searchTerm, dateFrom, dateTo, val);
                  }}
                >
                  <MenuItem value="all">{t('workOrders.filter.activityAll')}</MenuItem>
                  <MenuItem value="check_in">{t('workOrders.filter.activityCheckIn')}</MenuItem>
                  <MenuItem value="check_out">{t('workOrders.filter.activityCheckOut')}</MenuItem>
                  <MenuItem value="pause">{t('workOrders.filter.activityPause')}</MenuItem>
                  <MenuItem value="resume">{t('workOrders.filter.activityResume')}</MenuItem>
                </Select>
              </FormControl>
              {(dateFrom || dateTo || activityFilter !== 'all') && (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    setDateFrom('');
                    setDateTo('');
                    setActivityFilter('all');
                    fetchWorkOrders(1, statusFilter, searchTerm, '', '', 'all');
                  }}
                >
                  {t('workOrders.filter.clearFilters')}
                </Button>
              )}
              <Button
                variant="outlined"
                size="small"
                startIcon={sortOrder === 'desc' ? <ArrowDownward /> : <ArrowUpward />}
                onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                sx={{ minWidth: 140 }}
              >
                {sortOrder === 'desc' ? t('workOrders.sort.descending') : t('workOrders.sort.ascending')}
              </Button>
              {can('work-orders.export-excel') && (
                <Button variant="outlined" onClick={handleExportExcel}>
                  {t('workOrders.export.excel')}
                </Button>
              )}
              {can('work-orders.export-pdf') && (
                <Button variant="outlined" onClick={handleExportPDF}>
                  {t('workOrders.export.pdf')}
                </Button>
              )}
              {can('work-orders.add') && (
                <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>
                  {t('workOrders.form.dialog.addTitle')}
                </Button>
              )}
            </>
          )}
          {can('quick-check-ins.add') && !activeQuickCheckIn && (
            <Button variant="contained" color="secondary" onClick={handleQuickCheckInOpen}>
              {t('workOrders.quickCheckIn.button')}
            </Button>
          )}
          {can('quick-check-ins.add') && activeQuickCheckIn && (
            <Button variant="contained" color="warning" onClick={handleQuickCheckOutOpen}>
              {t('workOrders.quickCheckOut.button')}
            </Button>
          )}
        </Box>
      </Box>

      <Tabs value={activeTab} onChange={(e, newValue) => { setActiveTab(newValue); if (newValue === 2) fetchCheckInHistory(1); }} sx={{ mb: 3 }} variant="scrollable" scrollButtons="auto">
        <Tab icon={<ListIcon />} label={t('workOrders.tabs.list')} />
        <Tab icon={<MapIcon />} label={t('workOrders.tabs.map')} />
        {can('quick-check-ins.view') && (
          <Tab icon={<History />} label={t('workOrders.tabs.checkIns')} />
        )}
      </Tabs>

      {activeTab === 0 && (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Tabs 
            value={statusFilter} 
            onChange={(e, newValue) => handleStatusFilterChange(newValue)}
            sx={{ mb: 2 }}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab value="all" label={t('workOrders.statusTabs.all')} />
            <Tab value="pending" label={t('workOrders.statusTabs.pending')} />
            <Tab value="in_progress" label={t('workOrders.statusTabs.inProgress')} />
            <Tab value="completed" label={t('workOrders.statusTabs.completed')} />
            <Tab value="cancelled" label={t('workOrders.statusTabs.cancelled')} />
          </Tabs>

          {filteredWorkOrders.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary">
                {t('workOrders.noWorkOrders')}
              </Typography>
            </Box>
          ) : (
            filteredWorkOrders.map((workOrder) => {
          // For technicians, only show buttons for work orders assigned to them
          // Admins won't see check-in/out/pause/resume buttons, only edit/delete buttons
          const isAssigned = !!(user && (
            user.role === 'admin' || 
            (user.role === 'technician' && workOrder.technician && (
              (technicianId && workOrder.technician.id === technicianId) || 
              workOrder.technician.user?.id === user.id
            ))
          ));
          
          // Check if this work order is assigned to the current technician (for showing check-in/out buttons)
          const isAssignedToTechnician = !!(user?.role === 'technician' && workOrder.technician && (
            (technicianId && workOrder.technician.id === technicianId) || 
            workOrder.technician.user?.id === user.id
          ));
          return (
          <Card key={workOrder.id}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                <Box>
                  <Typography variant="h6">{language === 'ar' && workOrder.title_ar ? workOrder.title_ar : workOrder.title}</Typography>
                    <Typography variant="body2" color="text.secondary">#{workOrder.work_order_number}</Typography>
                </Box>
                <Box>
                    {can('work-orders.edit') && (
                      <IconButton size="small" onClick={() => handleOpen(workOrder)}><Edit /></IconButton>
                    )}
                    {can('work-orders.delete') && (
                      <IconButton size="small" onClick={() => handleDelete(workOrder.id)} color="error"><Delete /></IconButton>
                    )}
                    <Button size="small" onClick={() => navigate(`/work-orders/${workOrder.id}`)}>
                      {t('workOrders.actions.details')}
                    </Button>
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/work-orders/${workOrder.id}#expenses`)}
                      title="View Expenses"
                      color="primary"
                    >
                      <AttachMoney />
                    </IconButton>
                </Box>
              </Box>
              
              <Box display="flex" alignItems="center" mb={1}>
                <Home fontSize="small" color="action" sx={{ mr: 1 }} />
                <Typography variant="body2">
                  {language === 'ar' && workOrder.flat?.building?.name_ar ? workOrder.flat.building.name_ar : workOrder.flat?.building?.name} - {
                    workOrder.flats && workOrder.flats.length > 0
                      ? workOrder.flats.map(f => f.flat_number).join(', ')
                      : workOrder.flat?.flat_number
                  }
                </Typography>
              </Box>
              
              {workOrder.technician && (
                <Box display="flex" alignItems="center" mb={1}>
                  <Person fontSize="small" color="action" sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    {t('workOrders.card.technician')}: {(language === 'ar' && workOrder.technician.user?.name_ar ? workOrder.technician.user.name_ar : workOrder.technician.user?.name) || 'N/A'}
                  </Typography>
                </Box>
              )}

              {workOrder.amenities && workOrder.amenities.length > 0 && (
                <Box display="flex" alignItems="center" mb={1}>
                  <Star fontSize="small" color="action" sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    {t('workOrders.card.amenities')}: {workOrder.amenities.map(a => language === 'ar' ? a.name_ar : a.name_en).join(', ')}
                  </Typography>
                </Box>
              )}

              <Box display="flex" gap={1} mb={2}>
                  <Chip label={t(`workOrders.priority.${workOrder.priority}`)} size="small" color={getPriorityColor(workOrder.priority) as any} />
                  <Chip
                    label={t(`workOrders.status.${workOrder.status === 'in_progress' ? 'inProgress' : workOrder.status === 'paused' ? 'paused' : workOrder.status}`)}
                    size="small"
                    color={getStatusColor(workOrder.status) as any}
                  />
              </Box>

              {(workOrder.scheduled_from || workOrder.scheduled_to) && (
                <Box display="flex" alignItems="center" mb={1}>
                  <Schedule fontSize="small" color="action" sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    {workOrder.scheduled_from ? formatDateTime(workOrder.scheduled_from, 'en') : ''} — {workOrder.scheduled_to ? formatDateTime(workOrder.scheduled_to, 'en') : ''}
                  </Typography>
                </Box>
              )}

                <Typography variant="body2" mb={1}>{workOrder.description}</Typography>
                {!!workOrder.items && workOrder.items.length > 0 && (
                  <Box mb={1}>
                    <Typography variant="subtitle2">{t('workOrders.form.assignedItems')}</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5, alignItems: 'center' }}>
                      {workOrder.items.map(it => (
                        <Box key={it.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Chip label={`${it.item_name} x ${it.pivot?.quantity ?? 1}`} size="small" />
                          {can('work-orders.edit') && (
                            <IconButton
                              size="small"
                              onClick={() => {
                                setReturnItem({ workOrder, item: it });
                                setReturnQty((it.pivot?.quantity ?? 1).toString());
                                setReturnNotes('');
                                setReturnOpen(true);
                              }}
                              title="Return Item"
                              color="primary"
                            >
                              <Undo fontSize="small" />
                            </IconButton>
                    )}
                  </Box>
                      ))}
                </Box>
                </Box>
              )}
              
                {isAssigned && (
                  <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                    {isAssignedToTechnician && workOrder.status === 'pending' && can('work-orders.check-in') && (
                      <Button variant="outlined" onClick={() => handleCheckInOpen(workOrder)}>
                        {t('workOrders.checkIn.dialog.title')}
                      </Button>
                    )}
                    {isAssignedToTechnician && workOrder.status === 'paused' && can('work-orders.resume') && (
                      <Button variant="outlined" onClick={() => handleResumeOpen(workOrder)}>
                        {t('workOrders.resume.dialog.title')}
                      </Button>
                    )}
                    {isAssignedToTechnician && workOrder.status === 'in_progress' && can('work-orders.pause') && (
                      <Button variant="outlined" color="warning" onClick={() => handlePauseOpen(workOrder)}>
                        {t('workOrders.pause.dialog.title')}
                      </Button>
                    )}
                    {isAssignedToTechnician && (workOrder.status === 'in_progress' || workOrder.status === 'paused') && can('work-orders.check-out') && (
                      <Button variant="contained" onClick={() => handleCheckOutOpen(workOrder)}>
                        {t('workOrders.checkOut.dialog.title')}
                      </Button>
                    )}
                    {(can('expenses.add') || can('work-orders.add-expense')) && (
                      <Button 
                        variant="outlined" 
                        startIcon={<AttachMoney />}
                        onClick={() => navigate(`/work-orders/${workOrder.id}#add-expense`)}
                      >
                        {t('workOrders.actions.addExpense')}
                      </Button>
                    )}
                    {/* {can('work-orders.edit') && (
                      <Button variant="outlined" onClick={async () => {
                        setAssignWO(workOrder);
                        await fetchInventory();
                        setSelectedItemId('');
                        setSelectedQty('1');
                        setAssignOpen(true);
                      }}>
                        {t('workOrders.actions.assignProducts')}
                      </Button>
                )} */}
              </Box>
                )}
            </CardContent>
          </Card>
          );
        })
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 3, gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t('common.pagination.showing') || 'Showing'} {workOrders.length} {t('common.pagination.of') || 'of'} {totalCount} {t('common.pagination.items') || 'items'}
              </Typography>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </Box>
      )}

      {activeTab === 1 && (
        <Box sx={{ width: '100%', height: 'calc(100vh - 300px)', minHeight: 600 }}>
          {workOrdersWithLocations.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Typography variant="h6" color="text.secondary">
                {t('workOrders.map.noLocations')}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
              <WorkOrdersMapComponent workOrders={workOrdersWithLocations} onWorkOrderClick={(id) => navigate(`/work-orders/${id}`)} />
              {/* Overlay markers info */}
              <Box sx={{ position: 'absolute', top: 10, right: 10, bgcolor: 'background.paper', p: 2, borderRadius: 1, boxShadow: 2, maxWidth: 300, maxHeight: 400, overflow: 'auto', zIndex: 1000 }}>
                <Typography variant="h6" gutterBottom>Work Orders</Typography>
                {workOrdersWithLocations.map((wo: WorkOrder) => (
                  <Box key={wo.id} sx={{ mb: 1, pb: 1, borderBottom: '1px solid #eee', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }} onClick={() => navigate(`/work-orders/${wo.id}`)}>
                    <Typography variant="subtitle2">{language === 'ar' && wo.title_ar ? wo.title_ar : wo.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {language === 'ar' && wo.flat?.building?.name_ar ? wo.flat.building.name_ar : (wo.flat?.building?.name || 'Unknown Building')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {wo.work_order_number} - {t(`workOrders.status.${wo.status === 'in_progress' ? 'inProgress' : wo.status === 'paused' ? 'paused' : wo.status}`)}
                    </Typography>
                    <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
                      <Chip label={t(`workOrders.priority.${wo.priority}`)} size="small" color={getPriorityColor(wo.priority) as any} />
                      <Chip 
                        label={t(`workOrders.status.${wo.status === 'in_progress' ? 'inProgress' : wo.status === 'paused' ? 'paused' : wo.status}`)} 
                        size="small" 
                        color={getStatusColor(wo.status) as any} 
                      />
                    </Box>
                  </Box>
        ))}
      </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Quick Check-Ins Tab */}
      {activeTab === 2 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Active Quick Check-In Banner */}
          {activeQuickCheckIn && (
            <Card sx={{ mb: 1, border: '2px solid', borderColor: 'warning.main', bgcolor: 'warning.50' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" color="warning.dark">
                    {t('workOrders.quickCheckIn.activeTitle')}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{t('workOrders.quickCheckOut.building')}:</strong>{' '}
                    {language === 'ar' && activeQuickCheckIn.building?.name_ar
                      ? activeQuickCheckIn.building.name_ar
                      : activeQuickCheckIn.building?.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('workOrders.quickCheckOut.checkedInAt')}: {new Date(activeQuickCheckIn.check_in_time).toLocaleString()}
                  </Typography>
                  {activeQuickCheckIn.amenities && activeQuickCheckIn.amenities.length > 0 && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      {t('workOrders.quickCheckIn.selectAmenities').replace(' (optional)', '')}: {activeQuickCheckIn.amenities.map((a: any) => language === 'ar' ? a.name_ar : a.name_en).join(', ')}
                    </Typography>
                  )}
                  {activeQuickCheckIn.selected_flats && activeQuickCheckIn.selected_flats.length > 0 && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      {t('workOrders.quickCheckIn.selectFlats').replace(' (optional)', '')}: {activeQuickCheckIn.selected_flats.map((f: any) => f.flat_number).join(', ')}
                    </Typography>
                  )}
                </Box>
                <Button variant="contained" color="warning" onClick={handleQuickCheckOutOpen}>
                  {t('workOrders.quickCheckOut.button')}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Check-In History List */}
          {checkInHistory.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              {t('workOrders.checkInHistory.empty')}
            </Typography>
          ) : (
            checkInHistory.map((ci: any) => (
              <Card key={ci.id} sx={{ border: '1px solid', borderColor: ci.check_out_time ? 'divider' : 'success.main' }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {language === 'ar' && ci.building?.name_ar ? ci.building.name_ar : ci.building?.name || '-'}
                      </Typography>
                      {/* Technician name - visible to admins */}
                      {user?.role === 'admin' && ci.user && (
                        <Typography variant="caption" color="primary.main" display="block">
                          {language === 'ar' && ci.user.name_ar ? ci.user.name_ar : ci.user.name}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" display="block">
                        {t('workOrders.checkInHistory.checkIn')}: {new Date(ci.check_in_time).toLocaleString()}
                      </Typography>
                      {ci.check_out_time && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {t('workOrders.checkInHistory.checkOut')}: {new Date(ci.check_out_time).toLocaleString()}
                        </Typography>
                      )}
                      {ci.check_in_time && ci.check_out_time && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {t('workOrders.checkInHistory.duration')}: {(() => {
                            const ms = new Date(ci.check_out_time).getTime() - new Date(ci.check_in_time).getTime();
                            const h = Math.floor(ms / 3600000);
                            const m = Math.floor((ms % 3600000) / 60000);
                            return `${h}h ${m}m`;
                          })()}
                        </Typography>
                      )}
                      {ci.amenities && ci.amenities.length > 0 && (
                        <Box sx={{ mt: 0.5 }}>
                          {ci.amenities.map((a: any) => (
                            <Chip key={a.id} label={language === 'ar' ? a.name_ar : a.name_en} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                          ))}
                        </Box>
                      )}
                      {ci.selected_flats && ci.selected_flats.length > 0 && (
                        <Box sx={{ mt: 0.5 }}>
                          {ci.selected_flats.map((f: any) => (
                            <Chip key={f.id} label={f.flat_number} size="small" variant="outlined" sx={{ mr: 0.5, mb: 0.5 }} />
                          ))}
                        </Box>
                      )}
                      {ci.check_in_notes && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          {ci.check_in_notes}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      label={ci.check_out_time ? t('workOrders.checkInHistory.statusOut') : t('workOrders.checkInHistory.statusIn')}
                      color={ci.check_out_time ? 'default' : 'success'}
                      size="small"
                    />
                  </Box>
                </CardContent>
              </Card>
            ))
          )}
          {checkInHistoryTotalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              <Pagination
                count={checkInHistoryTotalPages}
                page={checkInHistoryPage}
                onChange={(_e, page) => fetchCheckInHistory(page)}
                size="small"
              />
            </Box>
          )}
        </Box>
      )}

      {/* Create/Edit Work Order */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          {editingWorkOrder ? t('workOrders.form.dialog.editTitle') : t('workOrders.form.dialog.addTitle')}
          {isMobile && (
            <IconButton onClick={handleClose} sx={{ position: 'absolute', right: 8, top: 8 }}><Close /></IconButton>
          )}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent dividers>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Autocomplete
                options={buildings}
                value={buildings.find(b => b.id.toString() === formData.building_id) || null}
                onChange={(_, newValue) => {
                  setFormData({
                    ...formData,
                    building_id: newValue ? newValue.id.toString() : '',
                    flat_ids: [], // Reset flats when building changes
                    amenity_ids: [] // Reset amenities when building changes
                  });
                }}
                getOptionLabel={(option) => language === 'ar' && option.name_ar ? option.name_ar : option.name}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('workOrders.form.building')}
                    required
                    placeholder={t('workOrders.form.searchBuilding')}
                  />
                )}
              />

              <Autocomplete
                multiple
                options={flats}
                value={flats.filter(f => formData.flat_ids.includes(f.id.toString()))}
                onChange={(_, newValue) => {
                  setFormData({ ...formData, flat_ids: newValue.map(f => f.id.toString()) });
                }}
                getOptionLabel={(option) => option.flat_number}
                disabled={!formData.building_id}
                disableCloseOnSelect
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox checked={selected} sx={{ mr: 1 }} />
                    {option.flat_number}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('workOrders.form.flat')}
                    placeholder={formData.building_id ? t('workOrders.form.searchFlats') : t('workOrders.form.selectBuildingFirst')}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option.flat_number}
                      size="small"
                      {...getTagProps({ index })}
                    />
                  ))
                }
              />

              <Autocomplete
                multiple
                options={buildingAmenities}
                value={buildingAmenities.filter(a => formData.amenity_ids.includes(a.id.toString()))}
                onChange={(_, newValue) => {
                  setFormData({ ...formData, amenity_ids: newValue.map(a => a.id.toString()) });
                }}
                getOptionLabel={(option) => language === 'ar' ? option.name_ar : option.name_en}
                disabled={!formData.building_id}
                disableCloseOnSelect
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox checked={selected} sx={{ mr: 1 }} />
                    {language === 'ar' ? option.name_ar : option.name_en}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('workOrders.form.amenities')}
                    placeholder={formData.building_id ? t('workOrders.form.searchAmenities') : t('workOrders.form.selectBuildingFirst')}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={language === 'ar' ? option.name_ar : option.name_en}
                      size="small"
                      {...getTagProps({ index })}
                    />
                  ))
                }
              />

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                <TextField fullWidth label={t('workOrders.form.title')} name="title" value={formData.title} onChange={handleInputChange} />
                <TextField fullWidth label={t('workOrders.form.titleAr')} name="title_ar" value={formData.title_ar} onChange={handleInputChange} />
              </Box>
              <TextField fullWidth label={t('workOrders.form.description')} name="description" value={formData.description} onChange={handleInputChange} multiline rows={2} />
              
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>{t('workOrders.form.priority')}</InputLabel>
                  <Select
                    value={formData.priority}
                    label={t('workOrders.form.priority')}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value as any })}
                  >
                    <MenuItem value="low">{t('workOrders.priority.low')}</MenuItem>
                    <MenuItem value="medium">{t('workOrders.priority.medium')}</MenuItem>
                    <MenuItem value="urgent">{t('workOrders.priority.urgent')}</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>{t('workOrders.form.status')}</InputLabel>
                  <Select
                    value={formData.status}
                    label={t('workOrders.form.status')}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                  >
                    <MenuItem value="pending">{t('workOrders.status.pending')}</MenuItem>
                    <MenuItem value="in_progress">{t('workOrders.status.inProgress')}</MenuItem>
                    <MenuItem value="paused">{t('workOrders.status.paused')}</MenuItem>
                    <MenuItem value="completed">{t('workOrders.status.completed')}</MenuItem>
                    <MenuItem value="cancelled">{t('workOrders.status.cancelled')}</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              
              <Autocomplete
                multiple
                options={technicians}
                value={technicians.filter(t => formData.technician_ids.includes(t.id.toString()))}
                onChange={(_, newValue) => {
                  const selected = newValue as Technician[];
                  setFormData({ ...formData, technician_ids: selected.map(t => t.id.toString()) });
                }}
                getOptionLabel={(option) => (language === 'ar' && option.user?.name_ar ? option.user.name_ar : option.user?.name) || 'N/A'}
                disableCloseOnSelect
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox checked={selected} sx={{ mr: 1 }} />
                    {(language === 'ar' && option.user?.name_ar ? option.user.name_ar : option.user?.name) || 'N/A'}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('workOrders.form.technician')}
                    placeholder={t('workOrders.form.searchTechnician')}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  (value as Technician[]).map((option, index) => (
                    <Chip
                      label={(language === 'ar' && option.user?.name_ar ? option.user.name_ar : option.user?.name) || 'N/A'}
                      size="small"
                      {...getTagProps({ index })}
                    />
                  ))
                }
              />
              
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                <TextField
                  fullWidth
                  label={t('workOrders.form.scheduledFrom')}
                  name="scheduled_from"
                  type="datetime-local"
                  value={formData.scheduled_from}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                  inputProps={editingWorkOrder ? undefined : { min: getCurrentDateTimeLocal() }}
                />
                <TextField
                  fullWidth
                  label={t('workOrders.form.scheduledTo')}
                  name="scheduled_to"
                  type="datetime-local"
                  value={formData.scheduled_to}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                  inputProps={editingWorkOrder ? undefined : { min: formData.scheduled_from || getCurrentDateTimeLocal() }}
                />
              </Box>
              
              <Box>
                <Typography variant="h6" gutterBottom>{t('workOrders.form.images.sectionTitle')}</Typography>
                <input type="file" accept="image/*" multiple onChange={handleImageSelect} style={{ display: 'none' }} id="image-upload" />
                <label htmlFor="image-upload">
                  <Button variant="outlined" component="span" startIcon={<Add />}>
                    {t('workOrders.form.images.add')}
                  </Button>
                </label>
                {(existingImages.length > 0 || imagePreview.length > 0) && (
                  <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {existingImages.map((image, index) => (
                      <Box key={`existing-${index}`} sx={{ position: 'relative' }}>
                        <img src={storageUrl(image)} alt={`Existing image ${index + 1}`} onClick={() => setPreviewImage(storageUrl(image))} style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 4, border: '1px solid #ddd', cursor: 'pointer' }} />
                        <IconButton size="small" onClick={() => removeExistingImage(index)} sx={{ position: 'absolute', top: -8, right: -8, backgroundColor: 'error.main', color: 'white', '&:hover': { backgroundColor: 'error.dark' } }}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                    {imagePreview.map((preview, index) => (
                      <Box key={`new-${index}`} sx={{ position: 'relative' }}>
                        <img src={preview} alt={`New image ${index + 1}`} onClick={() => setPreviewImage(preview)} style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 4, border: '1px solid #ddd', cursor: 'pointer' }} />
                        <IconButton size="small" onClick={() => removeNewImage(index)} sx={{ position: 'absolute', top: -8, right: -8, backgroundColor: 'error.main', color: 'white', '&:hover': { backgroundColor: 'error.dark' } }}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              <TextField fullWidth label={t('workOrders.form.notes')} name="notes" value={formData.notes} onChange={handleInputChange} multiline rows={2} />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={submitting}>{t('workOrders.form.cancel')}</Button>
            <Button type="submit" variant="contained" disabled={submitting} startIcon={submitting ? <CircularProgress size={20} /> : null}>
              {editingWorkOrder ? t('workOrders.form.update') : t('workOrders.form.create')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      {/* Assign Products Dialog */}
      <Dialog open={assignOpen} onClose={() => setAssignOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('workOrders.assign.dialog.title')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>{t('workOrders.assign.item.label')}</InputLabel>
              <Select
                value={selectedItemId}
                label={t('workOrders.assign.item.label')}
                onChange={(e) => setSelectedItemId(e.target.value)}
              >
                {inventory.map(it => (
                  <MenuItem key={it.id} value={String(it.id)}>
                    {it.item_name} {it.category ? `(${it.category})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label={t('workOrders.assign.qty.label')}
              type="number"
              value={selectedQty}
              onChange={(e) => {
                const next = e.target.value === '' ? '' : Math.max(1, Number(e.target.value));
                setSelectedQty(next.toString());
              }}
              inputProps={{ min: 1 }}
            />
            {!!assignWO?.items && assignWO.items.length > 0 && (
              <Box>
                <Typography variant="subtitle2">{t('workOrders.assign.currentlyAssigned')}</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                  {assignWO.items.map(it => (
                    <Box key={it.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Chip
                        label={`${it.item_name} x ${it.pivot?.quantity ?? 1}`}
                        onDelete={async () => {
                          try {
                            await api.delete(`/work-orders/${assignWO!.id}/items/${it.id}`);
                            const { data } = await api.get(`/work-orders/${assignWO!.id}/items`);
                            setAssignWO(prev => prev ? ({ ...prev, items: Array.isArray(data) ? data : [] }) : prev);
                            fetchWorkOrders();
                          } catch (e) { console.error(e); }
                          }}
                        />
                        <IconButton
                          size="small"
                        onClick={() => {
                          setReturnItem({ workOrder: assignWO!, item: it });
                          setReturnQty((it.pivot?.quantity ?? 1).toString());
                          setReturnNotes('');
                          setReturnOpen(true);
                        }}
                        title="Return Item"
                        color="primary"
                      >
                        <Undo fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                </Box>
                  </Box>
                )}
              </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignOpen(false)}>{t('workOrders.assign.close')}</Button>
          <Button variant="contained" onClick={async () => {
            if (!assignWO || !selectedItemId) return;
            try {
              await api.post(`/work-orders/${assignWO.id}/items`, {
                inventory_id: Number(selectedItemId),
                quantity: Math.max(1, Number(selectedQty) || 1),
              });
              const { data } = await api.get(`/work-orders/${assignWO.id}/items`);
              setAssignWO(prev => prev ? ({ ...prev, items: Array.isArray(data) ? data : [] }) : prev);
              fetchWorkOrders();
              setSelectedItemId('');
              setSelectedQty('1');
            } catch (e) {
              console.error('Failed to assign item', e);
            }
          }}>{t('workOrders.assign.add')}</Button>
        </DialogActions>
      </Dialog>

      {/* Return Item Dialog */}
      <Dialog open={returnOpen} onClose={() => setReturnOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('workOrders.return.dialog.title')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {returnItem && (
              <>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    <strong>{t('workOrders.return.item')}</strong> {returnItem.item.item_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>{t('workOrders.return.assignedQty')}</strong> {returnItem.item.pivot?.quantity ?? 1}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>{t('workOrders.return.workOrder')}</strong> {returnItem.workOrder.work_order_number}
                  </Typography>
                </Box>
                <TextField
                  label={t('workOrders.return.qtyLabel')}
                  type="number"
                  value={returnQty}
                  onChange={(e) => {
                    const next = e.target.value === '' ? '' : Math.max(1, Number(e.target.value));
                    setReturnQty(next.toString());
                  }}
                  inputProps={{ min: 1, max: returnItem.item.pivot?.quantity ?? 1 }}
                  required
                  helperText={t('workOrders.return.qtyHelper').replace('{{max}}', String(returnItem.item.pivot?.quantity ?? 1))}
                />
                <TextField
                  label={t('workOrders.return.notesLabel')}
                  multiline
                  rows={3}
                  value={returnNotes}
                  onChange={(e) => setReturnNotes(e.target.value)}
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReturnOpen(false)}>{t('workOrders.return.cancel')}</Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (!returnItem || !returnQty) return;
              try {
                await api.post(
                  `/work-orders/${returnItem.workOrder.id}/items/${returnItem.item.id}/return`,
                  {
                    quantity: parseInt(returnQty),
                    notes: returnNotes || null,
                  }
                );
                // Refresh work order items
                const { data } = await api.get(`/work-orders/${returnItem.workOrder.id}/items`);
                setAssignWO(prev => prev ? ({ ...prev, items: Array.isArray(data) ? data : [] }) : prev);
                fetchWorkOrders();
                setReturnOpen(false);
                setReturnItem(null);
                setReturnQty('');
                setReturnNotes('');
              } catch (e: any) {
                console.error('Failed to return item', e);
                if (e.response?.data?.message) {
                  alert(e.response.data.message);
                } else {
                  alert(t('workOrders.return.error.generic'));
                }
              }
            }}
            disabled={!returnQty || !returnItem || parseInt(returnQty) < 1 || (returnItem && parseInt(returnQty) > (returnItem.item.pivot?.quantity ?? 1))}
          >
            {t('workOrders.return.submit')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Check-In Dialog */}
      <Dialog open={checkInOpen} onClose={() => {
        if (stream) {
          stream.getTracks().forEach((track: MediaStreamTrack) => track.stop());
          setStream(null);
        }
        setCheckInOpen(false);
      }} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {t('workOrders.checkIn.dialog.title')}
          <IconButton onClick={() => { if (stream) { stream.getTracks().forEach((track: MediaStreamTrack) => track.stop()); setStream(null); } setCheckInOpen(false); }} sx={{ color: 'error.main' }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ overflowX: 'hidden' }}>
          <Typography variant="body2" sx={{ mb: 1 }}>{language === 'ar' && activeWO?.title_ar ? activeWO.title_ar : activeWO?.title}</Typography>
          <Typography variant="caption" color={(!location.lat || !location.lng) ? 'error' : 'text.secondary'}>
            {(!location.lat || !location.lng)
              ? t('workOrders.location.fetching')
              : t('workOrders.location.set')
                  .replace('{{lat}}', String(location.lat))
                  .replace('{{lng}}', String(location.lng))}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
            {t('workOrders.time.label').replace('{{time}}', checkInDisplayTime)}
          </Typography>
          {(() => {
            const bLat = activeWO?.flat?.building?.latitude;
            const bLng = activeWO?.flat?.building?.longitude;
            const cLat = location.lat ? parseFloat(location.lat) : NaN;
            const cLng = location.lng ? parseFloat(location.lng) : NaN;
            if (bLat != null && bLng != null && !isNaN(cLat) && !isNaN(cLng)) {
              const dist = distanceMeters(cLat, cLng, bLat, bLng);
              return (
                <Typography variant="caption" color={dist > checkDistance ? 'error' : 'text.secondary'} sx={{ display: 'block', mt: 0.5 }}>
                  {t('workOrders.distance.label')
                    .replace('{{distance}}', dist.toFixed(0))
                    .replace('{{limit}}', String(checkDistance))}
                </Typography>
              );
            }
            if (bLat == null || bLng == null) {
              return (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                  {t('workOrders.buildingLocationMissing')}
                </Typography>
              );
            }
            return null;
          })()}
          {/* Distance is controlled from Settings; no per-dialog override */}
          {!!location.lat && !!location.lng && (
            <Box sx={{ mt: 1 }}>
              <iframe
                title="checkin-map"
                width="100%"
                height="200"
                style={{ border: 0, borderRadius: 8, maxWidth: '100%' }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                src={`https://www.google.com/maps?q=${encodeURIComponent(location.lat)},${encodeURIComponent(location.lng)}&z=15&output=embed`}
              />
            </Box>
          )}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1">{t('workOrders.beforePhotos.title')}</Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
              <input type="file" multiple accept="image/*" id="before-upload" style={{ display: 'none' }} onChange={onBeforeImages} />
              <label htmlFor="before-upload">
                <Button variant="outlined" startIcon={<ImageIcon />} component="span" size={isMobile ? 'small' : 'medium'}>
                  {t('workOrders.photos.add')}
                </Button>
              </label>
              <Button
                variant="outlined"
                startIcon={<PhotoCamera />}
                onClick={() => openCamera('before')}
                size={isMobile ? 'small' : 'medium'}
              >
                {t('workOrders.photos.captureFromCamera')}
              </Button>
            </Box>
            {beforePreview.length > 0 && (
              <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {beforePreview.map((src, i) => (
                  <Box key={i} sx={{ position: 'relative' }}>
                    <img src={src} alt={`before-${i}`} style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4, border: '1px solid #ddd' }} />
                    <IconButton
                      size="small"
                      sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'error.main', color: 'white', width: 24, height: 24 }}
                      onClick={() => {
                        setBeforeImages(prev => prev.filter((_, idx) => idx !== i));
                        setBeforePreview(prev => {
                          URL.revokeObjectURL(prev[i]);
                          return prev.filter((_, idx) => idx !== i);
                        });
                      }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label={t('workOrders.notes.optional')}
              value={checkInNotes}
              onChange={(e) => setCheckInNotes(e.target.value)}
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCheckInOpen(false)}>{t('workOrders.checkIn.cancel')}</Button>
          <Button onClick={submitCheckIn} variant="contained" disabled={!location.lat || !location.lng}>
            {t('workOrders.checkIn.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Check-Out Dialog */}
      <Dialog open={checkOutOpen} onClose={() => {
        if (stream) {
          stream.getTracks().forEach((track: MediaStreamTrack) => track.stop());
          setStream(null);
        }
        setCheckOutOpen(false);
      }} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {t('workOrders.checkOut.dialog.title')}
          <IconButton onClick={() => { if (stream) { stream.getTracks().forEach((track: MediaStreamTrack) => track.stop()); setStream(null); } setCheckOutOpen(false); }} sx={{ color: 'error.main' }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ overflowX: 'hidden' }}>
          <Typography variant="body2" sx={{ mb: 1 }}>{language === 'ar' && activeWO?.title_ar ? activeWO.title_ar : activeWO?.title}</Typography>
          <Typography variant="caption" color={(!location.lat || !location.lng) ? 'error' : 'text.secondary'}>
            {(!location.lat || !location.lng)
              ? t('workOrders.location.fetching')
              : t('workOrders.location.set')
                  .replace('{{lat}}', String(location.lat))
                  .replace('{{lng}}', String(location.lng))}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
            {t('workOrders.time.label').replace('{{time}}', checkOutDisplayTime)}
          </Typography>
          {(() => {
            const bLat = activeWO?.flat?.building?.latitude;
            const bLng = activeWO?.flat?.building?.longitude;
            const cLat = location.lat ? parseFloat(location.lat) : NaN;
            const cLng = location.lng ? parseFloat(location.lng) : NaN;
            if (bLat != null && bLng != null && !isNaN(cLat) && !isNaN(cLng)) {
              const dist = distanceMeters(cLat, cLng, bLat, bLng);
              return (
                <Typography variant="caption" color={dist > checkDistance ? 'error' : 'text.secondary'} sx={{ display: 'block', mt: 0.5 }}>
                  {t('workOrders.distance.label')
                    .replace('{{distance}}', dist.toFixed(0))
                    .replace('{{limit}}', String(checkDistance))}
                </Typography>
              );
            }
            if (bLat == null || bLng == null) {
              return (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                  {t('workOrders.buildingLocationMissing')}
                </Typography>
              );
            }
            return null;
          })()}
          {/* Distance is controlled from Settings; no per-dialog override */}
          {!!location.lat && !!location.lng && (
            <Box sx={{ mt: 1 }}>
              <iframe
                title="checkout-map"
                width="100%"
                height="200"
                style={{ border: 0, borderRadius: 8, maxWidth: '100%' }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                src={`https://www.google.com/maps?q=${encodeURIComponent(location.lat)},${encodeURIComponent(location.lng)}&z=15&output=embed`}
              />
            </Box>
          )}
          <Box sx={{ mt: 1 }}>
            <Typography variant="subtitle1">{t('workOrders.afterPhotos.title')}</Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
              <input type="file" multiple accept="image/*" id="after-upload" style={{ display: 'none' }} onChange={onAfterImages} />
              <label htmlFor="after-upload">
                <Button variant="outlined" startIcon={<ImageIcon />} component="span" size={isMobile ? 'small' : 'medium'}>
                  {t('workOrders.photos.add')}
                </Button>
              </label>
              <Button
                variant="outlined"
                startIcon={<PhotoCamera />}
                onClick={() => openCamera('after')}
                size={isMobile ? 'small' : 'medium'}
              >
                {t('workOrders.photos.captureFromCamera')}
              </Button>
            </Box>
            {afterPreview.length > 0 && (
              <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {afterPreview.map((src, i) => (
                  <Box key={i} sx={{ position: 'relative' }}>
                    <img src={src} alt={`after-${i}`} style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4, border: '1px solid #ddd' }} />
                    <IconButton
                      size="small"
                      sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'error.main', color: 'white', width: 24, height: 24 }}
                      onClick={() => {
                        setAfterImages(prev => prev.filter((_, idx) => idx !== i));
                        setAfterPreview(prev => {
                          URL.revokeObjectURL(prev[i]);
                          return prev.filter((_, idx) => idx !== i);
                        });
                      }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label={t('workOrders.notes.optional')}
              value={checkOutNotes}
              onChange={(e) => setCheckOutNotes(e.target.value)}
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCheckOutOpen(false)}>{t('workOrders.checkOut.cancel')}</Button>
          <Button onClick={submitCheckOut} variant="contained" disabled={!location.lat || !location.lng}>
            {t('workOrders.checkOut.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Camera Capture Dialog */}
      <Dialog open={cameraOpen} onClose={closeCamera} maxWidth="sm" fullWidth>
        <DialogTitle>
          {t('workOrders.camera.dialog.titlePrefix')}
          {cameraMode === 'before' ? t('workOrders.camera.before') : t('workOrders.camera.after')}
          <IconButton
            aria-label="close"
            onClick={closeCamera}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ position: 'relative', width: '100%', bgcolor: 'black', borderRadius: 2, overflow: 'hidden', minHeight: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {stream ? (
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{ 
                  width: '100%', 
                  height: 'auto',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  display: 'block'
                }}
              />
            ) : (
              <Box sx={{ 
                color: 'white',
                textAlign: 'center',
                p: 3
              }}>
                <Typography variant="body1">{t('workOrders.camera.loading')}</Typography>
                <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                  {t('workOrders.camera.allowPrompt')}
                </Typography>
              </Box>
            )}
            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button onClick={closeCamera} variant="outlined">
            {t('workOrders.camera.cancel')}
          </Button>
          <Button 
            onClick={capturePhoto} 
            variant="contained" 
            startIcon={<PhotoCamera />}
            size="large"
            sx={{ minWidth: 150 }}
          >
            {t('workOrders.camera.capture')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pause Dialog */}
      <Dialog open={pauseOpen} onClose={() => setPauseOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {t('workOrders.pause.dialog.title')}
          <IconButton onClick={() => setPauseOpen(false)} sx={{ color: 'error.main' }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ overflowX: 'hidden' }}>
          <Typography variant="body2" sx={{ mb: 1 }}>{language === 'ar' && activeWO?.title_ar ? activeWO.title_ar : activeWO?.title}</Typography>
          <Typography variant="caption" color={(!pauseLocation.lat || !pauseLocation.lng) ? 'error' : 'text.secondary'}>
            {(!pauseLocation.lat || !pauseLocation.lng)
              ? t('workOrders.location.fetching')
              : t('workOrders.location.set')
                  .replace('{{lat}}', String(pauseLocation.lat))
                  .replace('{{lng}}', String(pauseLocation.lng))}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
            {t('workOrders.time.label').replace('{{time}}', pauseDisplayTime)}
          </Typography>
          {(() => {
            const bLat = activeWO?.flat?.building?.latitude;
            const bLng = activeWO?.flat?.building?.longitude;
            const cLat = pauseLocation.lat ? parseFloat(pauseLocation.lat) : NaN;
            const cLng = pauseLocation.lng ? parseFloat(pauseLocation.lng) : NaN;
            if (bLat != null && bLng != null && !isNaN(cLat) && !isNaN(cLng)) {
              const dist = distanceMeters(cLat, cLng, bLat, bLng);
              return (
                <Typography variant="caption" color={dist > checkDistance ? 'error' : 'text.secondary'} sx={{ display: 'block', mt: 0.5 }}>
                  {t('workOrders.distance.label')
                    .replace('{{distance}}', dist.toFixed(0))
                    .replace('{{limit}}', String(checkDistance))}
                </Typography>
              );
            }
            if (bLat == null || bLng == null) {
              return (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                  {t('workOrders.buildingLocationMissing')}
                </Typography>
              );
            }
            return null;
          })()}
          {!!pauseLocation.lat && !!pauseLocation.lng && (
            <Box sx={{ mt: 1 }}>
              <iframe
                title="pause-map"
                width="100%"
                height="200"
                style={{ border: 0, borderRadius: 8, maxWidth: '100%' }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                src={`https://www.google.com/maps?q=${encodeURIComponent(pauseLocation.lat)},${encodeURIComponent(pauseLocation.lng)}&z=15&output=embed`}
              />
            </Box>
          )}
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label={t('workOrders.pause.noteOptional')}
              value={pauseNotes}
              onChange={(e) => setPauseNotes(e.target.value)}
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPauseOpen(false)}>{t('workOrders.pause.cancel')}</Button>
          <Button onClick={submitPause} variant="contained" disabled={!pauseLocation.lat || !pauseLocation.lng}>
            {t('workOrders.pause.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resume Dialog */}
      <Dialog open={resumeOpen} onClose={() => setResumeOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {t('workOrders.resume.dialog.title')}
          <IconButton onClick={() => setResumeOpen(false)} sx={{ color: 'error.main' }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ overflowX: 'hidden' }}>
          <Typography variant="body2" sx={{ mb: 1 }}>{language === 'ar' && activeWO?.title_ar ? activeWO.title_ar : activeWO?.title}</Typography>
          <Typography variant="caption" color={(!resumeLocation.lat || !resumeLocation.lng) ? 'error' : 'text.secondary'}>
            {(!resumeLocation.lat || !resumeLocation.lng)
              ? t('workOrders.location.fetching')
              : t('workOrders.location.set')
                  .replace('{{lat}}', String(resumeLocation.lat))
                  .replace('{{lng}}', String(resumeLocation.lng))}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
            {t('workOrders.time.label').replace('{{time}}', resumeDisplayTime)}
          </Typography>
          {(() => {
            const bLat = activeWO?.flat?.building?.latitude;
            const bLng = activeWO?.flat?.building?.longitude;
            const cLat = resumeLocation.lat ? parseFloat(resumeLocation.lat) : NaN;
            const cLng = resumeLocation.lng ? parseFloat(resumeLocation.lng) : NaN;
            if (bLat != null && bLng != null && !isNaN(cLat) && !isNaN(cLng)) {
              const dist = distanceMeters(cLat, cLng, bLat, bLng);
              return (
                <Typography variant="caption" color={dist > checkDistance ? 'error' : 'text.secondary'} sx={{ display: 'block', mt: 0.5 }}>
                  {t('workOrders.distance.label')
                    .replace('{{distance}}', dist.toFixed(0))
                    .replace('{{limit}}', String(checkDistance))}
                </Typography>
              );
            }
            if (bLat == null || bLng == null) {
              return (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                  {t('workOrders.buildingLocationMissing')}
                </Typography>
              );
            }
            return null;
          })()}
          {!!resumeLocation.lat && !!resumeLocation.lng && (
            <Box sx={{ mt: 1 }}>
              <iframe
                title="resume-map"
                width="100%"
                height="200"
                style={{ border: 0, borderRadius: 8, maxWidth: '100%' }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                src={`https://www.google.com/maps?q=${encodeURIComponent(resumeLocation.lat)},${encodeURIComponent(resumeLocation.lng)}&z=15&output=embed`}
              />
            </Box>
          )}
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label={t('workOrders.resume.noteOptional')}
              value={resumeNotes}
              onChange={(e) => setResumeNotes(e.target.value)}
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResumeOpen(false)}>{t('workOrders.resume.cancel')}</Button>
          <Button onClick={submitResume} variant="contained" disabled={!resumeLocation.lat || !resumeLocation.lng}>
            {t('workOrders.resume.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

    {/* Add Category Dialog */}
    <Dialog open={categoryDialogOpen} onClose={() => setCategoryDialogOpen(false)} maxWidth="xs" fullWidth>
      <DialogTitle>Add Work Order Category</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Category Name"
            value={categoryForm.name}
            onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
            required
          />
          <TextField
            label="Description (optional)"
            value={categoryForm.description}
            onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))}
            multiline
            rows={2}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setCategoryDialogOpen(false)}>Cancel</Button>
        <Button variant="contained" onClick={handleAddCategory}>Save</Button>
      </DialogActions>
    </Dialog>

    {/* Quick Check-In Dialog */}
    <Dialog open={quickCheckInOpen} onClose={() => setQuickCheckInOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {t('workOrders.quickCheckIn.dialogTitle')}
        <IconButton onClick={() => setQuickCheckInOpen(false)} sx={{ color: 'error.main' }}><Close /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ overflowX: 'hidden' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* GPS Status */}
          <Typography variant="caption" color={(!quickLocation.lat || !quickLocation.lng) ? 'error' : 'text.secondary'}>
            {(!quickLocation.lat || !quickLocation.lng)
              ? t('workOrders.location.fetching')
              : t('workOrders.location.set')
                  .replace('{{lat}}', quickLocation.lat)
                  .replace('{{lng}}', quickLocation.lng)}
          </Typography>

          {/* Building selection */}
          {nearbyBuildings.length === 0 ? (
            <Typography color="error">{t('workOrders.quickCheckIn.noNearby')}</Typography>
          ) : (
            <Autocomplete
              options={nearbyBuildings}
              getOptionLabel={(option: Building) => {
                const name = language === 'ar' && option.name_ar ? option.name_ar : option.name;
                if (option.latitude && option.longitude && quickLocation.lat && quickLocation.lng) {
                  const dist = distanceMeters(
                    parseFloat(quickLocation.lat), parseFloat(quickLocation.lng),
                    option.latitude, option.longitude
                  );
                  return `${name} (${Math.round(dist)}m)`;
                }
                return name;
              }}
              value={quickBuilding}
              onChange={(_e, val) => handleQuickBuildingSelect(val)}
              renderInput={(params) => (
                <TextField {...params} label={t('workOrders.quickCheckIn.selectBuilding')} required />
              )}
            />
          )}

          {/* Flats multi-select */}
          {quickBuildingFlats.length > 0 && (
            <Autocomplete
              multiple
              options={quickBuildingFlats}
              getOptionLabel={(option: Flat) => option.flat_number}
              value={quickBuildingFlats.filter(f => quickFlatIds.includes(f.id))}
              onChange={(_e, val) => setQuickFlatIds(val.map(v => v.id))}
              renderInput={(params) => (
                <TextField {...params} label={t('workOrders.quickCheckIn.selectFlats')} />
              )}
            />
          )}

          {/* Amenities multi-select */}
          {quickBuildingAmenities.length > 0 && (
            <Autocomplete
              multiple
              options={quickBuildingAmenities}
              getOptionLabel={(option: any) => language === 'ar' ? option.name_ar : option.name_en}
              value={quickBuildingAmenities.filter((a: any) => quickAmenityIds.includes(a.id))}
              onChange={(_e, val) => setQuickAmenityIds(val.map((v: any) => v.id))}
              renderInput={(params) => (
                <TextField {...params} label={t('workOrders.quickCheckIn.selectAmenities')} />
              )}
            />
          )}

          {/* Notes */}
          <TextField
            label={t('workOrders.quickCheckIn.notes')}
            value={quickNotes}
            onChange={(e) => setQuickNotes(e.target.value)}
            multiline
            rows={2}
          />

          {/* Before images */}
          <Box>
            <Button variant="outlined" component="label" size="small" startIcon={<PhotoCamera />}>
              {t('workOrders.quickCheckIn.addImages')}
              <input type="file" hidden multiple accept="image/*" onChange={handleQuickBeforeImages} />
            </Button>
            {quickBeforePreview.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                {quickBeforePreview.map((src, i) => (
                  <Box key={i} sx={{ position: 'relative' }}>
                    <img src={src} alt="" style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4 }} />
                    <IconButton
                      size="small"
                      sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper' }}
                      onClick={() => {
                        setQuickBeforeImages(prev => prev.filter((_, idx) => idx !== i));
                        setQuickBeforePreview(prev => prev.filter((_, idx) => idx !== i));
                      }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setQuickCheckInOpen(false)}>{t('workOrders.form.cancel')}</Button>
        <Button
          variant="contained"
          onClick={submitQuickCheckIn}
          disabled={!quickBuilding || !quickLocation.lat || !quickLocation.lng || quickSubmitting}
        >
          {quickSubmitting ? <CircularProgress size={20} /> : t('workOrders.quickCheckIn.submit')}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Quick Check-Out Dialog */}
    <Dialog open={quickCheckOutOpen} onClose={() => setQuickCheckOutOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {t('workOrders.quickCheckOut.dialogTitle')}
        <IconButton onClick={() => setQuickCheckOutOpen(false)} sx={{ color: 'error.main' }}><Close /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ overflowX: 'hidden' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* Active check-in info */}
          {activeQuickCheckIn && (
            <Box>
              <Typography variant="body2">
                <strong>{t('workOrders.quickCheckOut.building')}:</strong>{' '}
                {language === 'ar' && activeQuickCheckIn.building?.name_ar
                  ? activeQuickCheckIn.building.name_ar
                  : activeQuickCheckIn.building?.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('workOrders.quickCheckOut.checkedInAt')}: {new Date(activeQuickCheckIn.check_in_time).toLocaleString()}
              </Typography>
            </Box>
          )}

          {/* GPS Status */}
          <Typography variant="caption" color={(!quickLocation.lat || !quickLocation.lng) ? 'error' : 'text.secondary'}>
            {(!quickLocation.lat || !quickLocation.lng)
              ? t('workOrders.location.fetching')
              : t('workOrders.location.set')
                  .replace('{{lat}}', quickLocation.lat)
                  .replace('{{lng}}', quickLocation.lng)}
          </Typography>

          {/* Notes */}
          <TextField
            label={t('workOrders.quickCheckOut.notes')}
            value={quickCheckOutNotes}
            onChange={(e) => setQuickCheckOutNotes(e.target.value)}
            multiline
            rows={2}
          />

          {/* After images */}
          <Box>
            <Button variant="outlined" component="label" size="small" startIcon={<PhotoCamera />}>
              {t('workOrders.quickCheckOut.addImages')}
              <input type="file" hidden multiple accept="image/*" onChange={handleQuickAfterImages} />
            </Button>
            {quickAfterPreview.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                {quickAfterPreview.map((src, i) => (
                  <Box key={i} sx={{ position: 'relative' }}>
                    <img src={src} alt="" style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4 }} />
                    <IconButton
                      size="small"
                      sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper' }}
                      onClick={() => {
                        setQuickAfterImages(prev => prev.filter((_, idx) => idx !== i));
                        setQuickAfterPreview(prev => prev.filter((_, idx) => idx !== i));
                      }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setQuickCheckOutOpen(false)}>{t('workOrders.form.cancel')}</Button>
        <Button
          variant="contained"
          color="warning"
          onClick={submitQuickCheckOut}
          disabled={quickSubmitting}
        >
          {quickSubmitting ? <CircularProgress size={20} /> : t('workOrders.quickCheckOut.submit')}
        </Button>
      </DialogActions>
    </Dialog>

      {/* Image Preview Dialog */}
      <Dialog open={!!previewImage} onClose={() => setPreviewImage(null)} maxWidth="lg">
        <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center', bgcolor: 'black' }}>
          <IconButton onClick={() => setPreviewImage(null)} sx={{ position: 'absolute', top: 8, right: 8, color: 'white', bgcolor: 'rgba(0,0,0,0.5)', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}>
            <Close />
          </IconButton>
          {previewImage && <img src={previewImage} alt="Preview" style={{ maxWidth: '100%', maxHeight: '90vh', objectFit: 'contain' }} />}
        </DialogContent>
      </Dialog>

    </Box>
  );
};

export default WorkOrders;